import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  ArrowLeft,
  Send,
  Loader2,
  Upload,
  RefreshCw,
  Globe,
  ChevronDown,
  Download,
  Trash2,
  FileText,
  Image,
  File,
  X,
  Check,
  CheckCircle,
  AlertCircle,
  Eye,
  Lock,
  Clock,
  Pause,
  FileSignature,
  Archive,
  XCircle,
  Copy,
  Sparkles,
  Crown,
  Info,
  Video,
  DollarSign,
  Calendar,
  Volume2, Printer} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { StatusBadge } from '../components/common/StatusBadge';
import { Skeleton } from '../components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../hooks/useWebSocket';
import { useTranslation } from '../i18n';
import { getContractLanguages, isContractLanguageSupported, CLARIFICATION_COPY } from '../lib/languages';
import SignaturePad from '../components/SignaturePad';
import SignaturesListModal from '../components/SignaturesListModal';
import DealInvoiceModal from '../components/DealInvoiceModal';
import PaymentStatusPanel from '../components/PaymentStatusPanel';
import { canWrite, canManageTeam, canManageBilling, isViewer } from '../lib/roleUtils';

// Get Tier 1 languages for contracts
const CONTRACT_LANGUAGES = getContractLanguages();

/**
 * DealRoomPage Component
 * Per PocketFiler Deal Rooms Spec:
 * - Header with deal info + status
 * - Timeline/Activity panel
 * - Messaging panel with translation
 * - Documents panel with AI analysis
 * - Status transitions with validation
 * - Read-only mode for closed/cancelled deals
 * - Uses SVG icons from asset pack, no emojis
 * - Standardized AI labels per spec
 */

// SVG Icon component
const SvgIcon = ({ src, className = 'h-5 w-5' }) => (
  <img src={`/assets/${src}`} alt="" className={className} aria-hidden="true" />
);

/** Parse API timestamp (UTC, often without Z suffix) to Date. Use for consistent timezone handling across Deal Room. */
function parseUtcTimestamp(ts) {
  if (ts == null) return null;
  const str = typeof ts === 'string' ? (ts.endsWith('Z') ? ts : `${ts}Z`) : ts;
  const date = new Date(str);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Format for Activity Timeline: full local date/time. */
function formatTimelineTimestamp(ts) {
  const date = parseUtcTimestamp(ts);
  return date ? date.toLocaleString() : '';
}

/** Format for Deal Details, Documents, Contract: date only. */
function formatDealRoomDate(ts) {
  const date = parseUtcTimestamp(ts);
  return date ? date.toLocaleDateString() : '';
}

/** Format for Messages tab: time only (e.g. 2:30 PM). */
function formatDealRoomTime(ts) {
  const date = parseUtcTimestamp(ts);
  return date ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
}

// Status color mapping
const statusColors = {
  draft: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600',
  pending_documents: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  active: 'bg-blue-100 text-blue-700 border-blue-300',
  on_hold: 'bg-orange-100 text-orange-700 border-orange-300',
  pending_signature: 'bg-purple-100 text-purple-700 border-purple-300',
  pending_payment: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  escrow_funded: 'bg-blue-100 text-blue-700 border-blue-300',
  release_requested: 'bg-indigo-100 text-indigo-700 border-indigo-300',
  disputed: 'bg-orange-100 text-orange-700 border-orange-300',
  completed: 'bg-green-100 text-green-700 border-green-300',
  closed: 'bg-gray-200 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-400 dark:border-gray-600',
  cancelled: 'bg-red-100 text-red-700 border-red-300'
};

// Status icons
const statusIcons = {
  draft: FileText,
  pending_documents: Clock,
  active: CheckCircle,
  on_hold: Pause,
  pending_signature: FileSignature,
  pending_payment: DollarSign,
  escrow_funded: Lock,
  release_requested: DollarSign,
  disputed: AlertCircle,
  completed: CheckCircle,
  closed: Archive,
  cancelled: XCircle
};

// Helper functions for status-based UI behavior
const isReadOnly = (status) => ['closed', 'cancelled', 'completed'].includes(status);
const isPendingSignature = (status) => status === 'pending_signature';
const isOnHold = (status) => status === 'on_hold';
const canUploadDocs = (statusInfo) => statusInfo?.can_upload_docs !== false;
const canSendMessages = (statusInfo) => statusInfo?.can_send_messages !== false;
const canEditDetails = (statusInfo) => statusInfo?.can_edit_details !== false;

// Timeline icon mapping
const timelineIcons = {
  deal_created: 'briefcase.svg',
  status_change: 'goal.svg',
  document_uploaded: 'clipboard.svg',
  document_deleted: 'clipboard.svg', // Using clipboard as trash icon is not verified
  contract_generated: 'clipboard.svg',
  signature_added: 'clipboard.svg',
  document_analyzed: 'bulb.svg',
  message_sent: 'chat.svg',
  // Payment events
  invoices_created: 'clipboard.svg',
  escrow_funded: 'goal.svg',
  escrow_partial: 'clock.svg',
  dispute_raised: 'shield.svg',
  dispute_resolved: 'goal.svg',
  release_requested: 'clock.svg',
  payment_release_approved: 'goal.svg',
  payment_withdrawn: 'goal.svg',
  default: 'clock.svg'
};

export default function DealRoomPage() {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { t, language } = useTranslation();
  const [messageInput, setMessageInput] = useState('');
  const [expandedMessages, setExpandedMessages] = useState({});
  const [suggestingReply, setSuggestingReply] = useState(false);
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Document detail sheet state
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentSheetOpen, setDocumentSheetOpen] = useState(false);
  const [documentPreviewUrl, setDocumentPreviewUrl] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // Status transition state
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedNewStatus, setSelectedNewStatus] = useState(null);
  const [transitionReason, setTransitionReason] = useState('');

  // Contract state
  const [contractDialogOpen, setContractDialogOpen] = useState(false);
  const [contractLanguages, setContractLanguages] = useState(['en', 'none']);
  const [contractSpecialTerms, setContractSpecialTerms] = useState('');
  const [selectedContractLanguage, setSelectedContractLanguage] = useState('en');
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);

  // Document analysis warning state
  const [analysisWarningOpen, setAnalysisWarningOpen] = useState(false);
  const [pendingAnalysisData, setPendingAnalysisData] = useState(null);
  const [isAnalyzingDocs, setIsAnalyzingDocs] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState({ current: 0, total: 0 });

  // Signature state
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [signingContract, setSigningContract] = useState(false);
  const [signaturesListOpen, setSignaturesListOpen] = useState(false);

  // Contract edit state
  const [isEditingContract, setIsEditingContract] = useState(false);
  const [editedContractContent, setEditedContractContent] = useState({});

  // Manual contract creation dialog (Basic plan)
  const [manualContractDialogOpen, setManualContractDialogOpen] = useState(false);
  const [finalizeConfirmOpen, setFinalizeConfirmOpen] = useState(false);
  const [generatingLoaderDismissed, setGeneratingLoaderDismissed] = useState(false);

  
  // Fetch deal data
  const { data: deal, isLoading: dealLoading } = useQuery({
    queryKey: ['deal', dealId],
    queryFn: () => api.get(`/deals/${dealId}`)
  });

  // Fetch deal status info (allowed transitions)
  const { data: statusInfo } = useQuery({
    queryKey: ['deal-status-info', dealId],
    queryFn: () => api.get(`/deals/${dealId}/status-info`),
    enabled: !!dealId
  });

  // Fetch organization plan info
  const { data: orgPlanInfo } = useQuery({
    queryKey: ['org-plan', deal?.organization_id],
    queryFn: () => api.get(`/organizations/${deal.organization_id}/plan`),
    enabled: !!deal?.organization_id
  });

  // Fetch deal contract
  const { data: contract, isLoading: contractLoading, refetch: refetchContract } = useQuery({
    queryKey: ['deal-contract', dealId],
    queryFn: () => api.get(`/deals/${dealId}/contracts`),
    enabled: !!dealId
  });

  // TTS State tracking variables
  const [ttsPlayingMsgId, setTtsPlayingMsgId] = useState(null);
  const [ttsPlayingDocId, setTtsPlayingDocId] = useState(null);

  const handleReadAloudMessage = async (msg) => {
    const text = msg.original_text || msg.translated_text || '';
    if (!text.trim()) return;
    const lang = user?.preferred_language || 'en';
    setTtsPlayingMsgId(msg._id);
    try {
      const res = await api.post('/ai/tts', { text: text.slice(0, 5000), lang });
      const audio = new Audio(api.getAudioUrl(res.audio_url));
      audio.onended = () => setTtsPlayingMsgId(null);
      audio.onerror = () => { toast.error(t('messages.readAloud.error') || 'Playback failed'); setTtsPlayingMsgId(null); };
      await audio.play();
    } catch (err) {
      toast.error(err.message || t('messages.readAloud.error') || 'Read aloud failed');
      setTtsPlayingMsgId(null);
    }
  };

  const handleReadAloudDocument = async (doc) => {
    let summaryText = '';

    if (doc.ai_extracted_data?.summary) {
      summaryText = doc.ai_extracted_data.summary;
    } else if (doc.ai_extracted_data?.raw_analysis) {
      try {
        const cleanJson = doc.ai_extracted_data.raw_analysis.replace(/```json/g, '').replace(/```/g, '').trim();
        const parsedData = JSON.parse(cleanJson);
        if (parsedData?.summary) {
          summaryText = parsedData.summary;
        }
      } catch (error) {
        console.error("Failed to parse raw_analysis for summary", error);
      }
    }

    if (!summaryText && doc.status === 'processed') {
      summaryText = `This is a ${doc.original_filename} document. No summary available.`;
    }

    const text = summaryText.trim();

    if (!text || text.length < 2) {
      toast.info(t('documents.readAloud.analyzeFirst') || 'Analyze this document first to read it aloud.');
      return;
    }
    const lang = user?.preferred_language || 'en';
    setTtsPlayingDocId(doc._id);
    try {
      const res = await api.post('/ai/tts', { text: text.slice(0, 5000), lang });
      const audio = new Audio(api.getAudioUrl(res.audio_url));
      audio.onended = () => setTtsPlayingDocId(null);
      audio.onerror = () => { toast.error(t('documents.readAloud.error') || 'Playback failed'); setTtsPlayingDocId(null); };
      await audio.play();
    } catch (err) {
      toast.error(err.message || t('documents.readAloud.error') || 'Read aloud failed');
      setTtsPlayingDocId(null);
    }
  };

  // Fetch deal timeline
  const { data: timeline, refetch: refetchTimeline } = useQuery({
    queryKey: ['deal-timeline', dealId],
    queryFn: () => api.get(`/deals/${dealId}/timeline`),
    enabled: !!dealId
  });

  // Scheduled events (calls/recordings)
  const { data: scheduledEvents = [], refetch: refetchScheduled } = useQuery({
    queryKey: ['scheduled-events', dealId],
    queryFn: () => api.get(`/scheduled-events/deal/${dealId}`),
    enabled: !!dealId
  });
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleType, setScheduleType] = useState('call');
  const [scheduleDateTime, setScheduleDateTime] = useState('');
  const createScheduleMutation = useMutation({
    mutationFn: (payload) => api.post('/scheduled-events', payload),
    onSuccess: () => {
      queryClient.invalidateQueries(['scheduled-events', dealId]);
      setScheduleModalOpen(false);
      setScheduleDateTime('');
      toast.success(t('schedule.created') || 'Scheduled');
    },
    onError: (err) => toast.error(err.message || 'Failed to schedule'),
  });
  const cancelScheduleMutation = useMutation({
    mutationFn: (eventId) => api.delete(`/scheduled-events/${eventId}`),
    onSuccess: () => queryClient.invalidateQueries(['scheduled-events', dealId]),
    onError: (err) => toast.error(err.message || 'Failed to cancel'),
  });

  // Fetch deal documents
  const { data: documents, isLoading: docsLoading } = useQuery({
    queryKey: ['deal-documents', dealId],
    queryFn: () => api.get(`/documents?deal_id=${dealId}`)
  });

  // Get or create deal conversation
  const { data: conversation } = useQuery({
    queryKey: ['deal-conversation', dealId],
    queryFn: () => api.post(`/deals/${dealId}/conversation`),
    enabled: !!dealId
  });

  // Access control: Check if current user can generate contracts
  // Only organization members (creators) can generate, not clients
  const canGenerateContract = user && deal && user.organization_id === deal.organization_id;

  // Access control: Only deal creator org members can create invoices
  const canCreateInvoice = user && deal && user.organization_id === deal.organization_id;

  // Invoice modal state
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);

  // Fetch deal payment invoices (visible when deal is in escrow flow)
  const { data: dealInvoicesData } = useQuery({
    queryKey: ['deal-invoices', dealId],
    queryFn: () => api.get(`/payments/invoices/deal/${dealId}`),
    enabled: !!dealId && ['pending_payment', 'escrow_funded', 'release_requested', 'disputed', 'completed'].includes(deal?.status),
  });
  const dealInvoices = dealInvoicesData?.invoices || [];

  // Handle Stripe redirect back (?payment=success|cancelled|stripe=success|stripe=refresh)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get('payment');
    const stripeStatus = params.get('stripe');
    const stripePending = params.get('stripe_pending');

    if (paymentStatus === 'success') {
      toast.success('Payment received! Funds are now held in escrow.');
      queryClient.invalidateQueries(['deal-invoices', dealId]);
      queryClient.invalidateQueries(['deal', dealId]);
    } else if (paymentStatus === 'cancelled') {
      toast.info('Payment was cancelled. You can try again from the payment panel.');
    } else if (paymentStatus === 'failed') {
      const stripeError = params.get('stripe_error');
      toast.error('Payment transfer failed! ' + (stripeError ? decodeURIComponent(stripeError) : 'Please contact support.'));
    }

    // Stripe Connect onboarding return (withdrawal flow)
    if (stripeStatus === 'success') {
      if (stripePending) {
        toast.info('Bank account setup is still pending. Please try withdrawing again once your account is verified.');
      } else {
        toast.success('Bank account connected! Your payment has been transferred.');
      }
      queryClient.invalidateQueries(['deal-invoices', dealId]);
      queryClient.invalidateQueries(['deal', dealId]);
      queryClient.invalidateQueries(['deal-timeline', dealId]);
    } else if (stripeStatus === 'refresh') {
      toast.info('Onboarding session expired. Please click "Withdraw Payment" again to restart.');
    } else if (stripeStatus === 'account_reset') {
      toast.info(
        'Your Stripe account connection was reset (access was revoked or the account no longer exists). ' +
        'Please click "Withdraw Payment" again to set up a new bank account.',
        { duration: 8000 }
      );
      queryClient.invalidateQueries(['deal-invoices', dealId]);
    }

    if (paymentStatus || stripeStatus) {
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch messages with polling
  const { data: messages, isLoading: messagesLoading, refetch: refetchMessages } = useQuery({
    queryKey: ['deal-messages', conversation?._id],
    queryFn: () => conversation?._id ? api.get(`/conversations/${conversation._id}/messages`) : [],
    enabled: !!conversation?._id,
    staleTime: Infinity // Don't refetch automatically, let WebSocket handle updates
  });

  // Fetch active call status

  // WebSocket integration
  const { lastMessage, isConnected, error: wsError, sendMessage } = useWebSocket(conversation?._id);
  const [typingUsers, setTypingUsers] = useState({});
  const typingTimeoutRef = useRef({});

  // Handle incoming WebSocket messages
  useEffect(() => {
    if (lastMessage && conversation?._id) {
      // Incoming call toast: show when someone else started the call and we're not in call
      if (lastMessage.type === 'call_ended') {
        hasShownActiveCallToastRef.current = false;
      }

      // Check for call events to invalidate active status query
      if (lastMessage.type === 'call_started' || lastMessage.type === 'call_ended') {
          queryClient.invalidateQueries(['active-call-status', dealId]);
      }

      // Handle typing events
      if (lastMessage.type === 'typing') {
        // Don't show typing for self
        if (lastMessage.user_id === user?._id) return;

        setTypingUsers(prev => ({ ...prev, [lastMessage.user_id]: lastMessage.user_name }));

        // Clear existing timeout for this user
        if (typingTimeoutRef.current[lastMessage.user_id]) {
          clearTimeout(typingTimeoutRef.current[lastMessage.user_id]);
        }

        // Set new timeout to clear typing status (backup in case stop_typing is missed)
        // Increased to 6s to allow for a missing heartbeat
        typingTimeoutRef.current[lastMessage.user_id] = setTimeout(() => {
          setTypingUsers(prev => {
            const newState = { ...prev };
            delete newState[lastMessage.user_id];
            return newState;
          });
        }, 6000);
        return;
      }

      // Handle stop_typing events
      if (lastMessage.type === 'stop_typing') {
        if (lastMessage.user_id === user?._id) return;

        setTypingUsers(prev => {
          const newState = { ...prev };
          delete newState[lastMessage.user_id];
          return newState;
        });

        if (typingTimeoutRef.current[lastMessage.user_id]) {
          clearTimeout(typingTimeoutRef.current[lastMessage.user_id]);
        }
        return;
      }

      // Handle standard chat messages
      if (!lastMessage.type || lastMessage.type === 'message' || lastMessage.type === 'call_started' || lastMessage.type === 'call_ended') {
        
        queryClient.setQueryData(['deal-messages', conversation._id], (oldMessages) => {
          if (!oldMessages) return [lastMessage];
          // Check duplication
          if (oldMessages.find(m => m._id === lastMessage._id)) return oldMessages;
          return [...oldMessages, lastMessage];
        });
      }
    }
  }, [lastMessage, conversation?._id, queryClient, user?._id, dealId]);

  // On load: if call already active and user not in call, show "call in progress" toast once

  // Handle typing heartbeat
  useEffect(() => {
    let interval;
    if (messageInput && conversation?._id && sendMessage) {
      // Function to report typing status
      const reportTyping = () => {
        sendMessage({
          type: 'typing',
          user_id: user?._id,
          user_name: user?.name || user?.email || 'User'
        });
      };

      // Report immediately and then every 3 seconds
      reportTyping();
      interval = setInterval(reportTyping, 3000);
    } else if (conversation?._id && sendMessage && !messageInput) {
      // If input cleared (and we were previously typing? We can just send stop safely)
      // To avoid spamming regarding dependency changes, we could check if we were typing, but sending one stop is fine.
      // For simplicity, we won't track "wasTyping" here to avoid complex refs, 
      // but we rely on the fact that this effect runs when messageInput changes to empty.
      // We can't easily detect "change to empty" vs "initial empty" without ref.
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [messageInput, conversation?._id, sendMessage, user]);

  // Send stop typing when input is cleared or message sent
  // We'll handle this in handleSendMessage and a separate effect for clearing
  const prevInputRef = useRef(messageInput);
  useEffect(() => {
    if (prevInputRef.current && !messageInput && conversation?._id && sendMessage) {
      sendMessage({
        type: 'stop_typing',
        user_id: user?._id
      });
    }
    prevInputRef.current = messageInput;
  }, [messageInput, conversation?._id, sendMessage, user]);

  /* Removed handleTyping callback */

  // Fetch AI summary
  const { data: aiSummary, refetch: refetchSummary, isFetching: summaryLoading } = useQuery({
    queryKey: ['deal-summary', dealId],
    queryFn: () => api.post(`/deals/${dealId}/summary`),
    enabled: !!dealId,
    staleTime: 60000 // Cache for 1 minute
  });
  // Meetings linked to this deal
  const { data: dealMeetings = [], refetch: refetchMeetings } = useQuery({
    queryKey: ['deal-meetings', dealId],
    queryFn: () => api.get(`/meetings?deal_id=${dealId}`).then(res => res.meetings || res || []),
    enabled: !!dealId,
  });


  // Fetch clients
  const { data: clients } = useQuery({
    queryKey: ['deal-clients', deal?.client_ids, deal?.client_id],
    queryFn: async () => {
      const ids = [];
      if (deal?.client_ids && deal.client_ids.length > 0) {
        ids.push(...deal.client_ids);
      } else if (deal?.client_id) {
        ids.push(deal.client_id);
      }

      if (ids.length === 0) return [];

      const responses = await Promise.all(ids.map(id => api.get(`/clients/${id}`).catch(() => null)));
      return responses.filter(Boolean);
    },
    enabled: !!(deal?.client_ids?.length || deal?.client_id)
  });

  // Scroll to bottom when new messages arrive (within message list only)
  const scrollToBottom = useCallback(() => {
    if (scrollContainerRef.current) {
      requestAnimationFrame(() => {
        const el = scrollContainerRef.current;
        if (el) {
          el.scrollTop = el.scrollHeight;
        }
      });
    }
  }, []);
  useEffect(() => {
    scrollToBottom();
  }, [messages, typingUsers, scrollToBottom]);

  const [fullScreenPreviewOpen, setFullScreenPreviewOpen] = useState(false);

  // Fetch presigned URL for S3 document preview (image and PDF)
  useEffect(() => {
    if ((!documentSheetOpen && !fullScreenPreviewOpen) || !selectedDocument) {
      // Keep URL if full screen is open
      if (!fullScreenPreviewOpen) setDocumentPreviewUrl(null);
      return;
    }
    const isS3 = selectedDocument?.storage_path?.startsWith?.('uploads/');
    const isImage = selectedDocument?.mime_type?.startsWith?.('image/');
    const isPdf = selectedDocument?.mime_type === 'application/pdf';

    if (!isS3 || (!isImage && !isPdf)) {
      if (!fullScreenPreviewOpen) setDocumentPreviewUrl(null); // Clear if not S3/Image/PDF, unless FS open (optimization?)
      // Actually if not S3, we use local URL construction, so we don't need presigned URL.
      setDocumentPreviewUrl(null);
      return;
    }

    // If we already have a URL for this document, don't refetch
    // (Optimization could be added here, but simple check is fine)

    let cancelled = false;
    api.getDocumentPresignedUrl(selectedDocument._id).then((url) => {
      if (!cancelled) setDocumentPreviewUrl(url);
    }).catch(() => {
      if (!cancelled) setDocumentPreviewUrl(null);
    });
    return () => { cancelled = true; };
  }, [documentSheetOpen, fullScreenPreviewOpen, selectedDocument?._id, selectedDocument?.storage_path, selectedDocument?.mime_type]);

  // Update deal details (title, summary)
  const updateDealMutation = useMutation({
    mutationFn: (data) => api.put(`/deals/${dealId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['deal', dealId]);
      queryClient.invalidateQueries(['deal-timeline', dealId]);
      toast.success('Deal updated');
    }
  });

  // Status transition mutation
  const statusTransitionMutation = useMutation({
    mutationFn: ({ newStatus, reason }) =>
      api.patch(`/deals/${dealId}/status`, { new_status: newStatus, reason }),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['deal', dealId]);
      queryClient.invalidateQueries(['deal-status-info', dealId]);
      queryClient.invalidateQueries(['deal-timeline', dealId]);
      setStatusDialogOpen(false);
      setSelectedNewStatus(null);
      setTransitionReason('');

      const toStatus = data.transition?.to?.replace('_', ' ') || '';
      toast.success(`Deal status changed to ${toStatus}`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to change status');
    }
  });

  // Handle status change initiation
  const handleStatusChangeClick = (newStatus) => {
    setSelectedNewStatus(newStatus);
    setStatusDialogOpen(true);
  };

  // Confirm status transition
  const confirmStatusTransition = () => {
    if (!selectedNewStatus) return;
    statusTransitionMutation.mutate({
      newStatus: selectedNewStatus.status,
      reason: transitionReason || undefined
    });
  };

  // Contract generation mutation
  const generateContractMutation = useMutation({
    mutationFn: (data) => api.post(`/deals/${dealId}/contracts/generate`, data),
    onSuccess: (data) => {
      console.log('Contract generation response:', data);
      // Check if response indicates pending analysis
      if (data?.status === 'pending_analysis') {
        console.log('Pending analysis detected, showing warning dialog');
        setPendingAnalysisData(data);
        setContractDialogOpen(false);
        setGeneratingLoaderDismissed(false);
        setAnalysisWarningOpen(true);
      } else {
        console.log('Normal contract generation success');
        // Normal contract generation success
        queryClient.invalidateQueries(['deal-contract', dealId]);
        queryClient.invalidateQueries(['deal-timeline', dealId]);
        setContractDialogOpen(false);
        setContractSpecialTerms('');
        setGeneratingLoaderDismissed(false);
        toast.success('Contract draft generated successfully');
      }
    },
    onError: (error) => {
      console.error('Contract generation error:', error);
      const detail = error.response?.data?.detail || '';
      if (error.response?.status === 403) {
        // Plan upgrade required
        setContractDialogOpen(false);
        setGeneratingLoaderDismissed(false);
        setUpgradeDialogOpen(true);
      } else {
        toast.error(detail || 'Could not generate contract. Please try again.');
        setContractDialogOpen(true);
        setGeneratingLoaderDismissed(false);
      }
    }
  });

  // Handle contract generation
  const handleGenerateContract = () => {
    // Check if at least one document is uploaded
    if (!documents || documents.length === 0) {
      toast.error("Kindly upload at least 1 document first");
      return;
    }

    // Plan-based access control: contract generation is Pro-only
    if (orgPlanInfo?.plan && orgPlanInfo.plan !== 'pro') {
      setUpgradeDialogOpen(true);
      return;
    }
    setContractDialogOpen(true);
  };

  // Submit contract generation
  const submitContractGeneration = (force = false) => {
    // 1. Check for unanalyzed documents first (if not forcing)
    if (!force) {
      const allDocs = documents || [];
      const unanalyzed = allDocs.filter(d => !d.ai_extracted_data);
      const analyzed = allDocs.filter(d => d.ai_extracted_data);

      if (unanalyzed.length > 0) {
        setPendingAnalysisData({
          message: t('dealRoom.contract.analysisWarning') || "Some documents haven't been analyzed yet. AI analysis improves contract accuracy.",
          analyzed_documents: analyzed.map(d => ({ id: d._id, filename: d.original_filename })),
          unanalyzed_documents: unanalyzed.map(d => ({ id: d._id, filename: d.original_filename }))
        });
        setAnalysisWarningOpen(true);
        setContractDialogOpen(false); // Close the generation dialog temporarily
        return;
      }
    }

    // 2. Proceed with generation — close dialogs so dedicated loader view is visible
    setContractDialogOpen(false);
    setAnalysisWarningOpen(false);
    setGeneratingLoaderDismissed(false);
    const validLanguages = contractLanguages.filter(l => l !== 'none');
    generateContractMutation.mutate({
      contract_type: 'purchase_agreement', // Fixed for now
      languages: validLanguages.length > 0 ? validLanguages : ['en'], // Fallback to EN if all none
      special_terms: contractSpecialTerms,
      force_generate: force
    });
  };

  // Handle running analysis and then generating contract
  const handleRunAnalysisAndGenerate = async () => {
    const unanalyzedDocs = pendingAnalysisData?.unanalyzed_documents || [];
    setIsAnalyzingDocs(true);
    setAnalysisProgress({ current: 0, total: unanalyzedDocs.length });

    let successCount = 0;
    for (let i = 0; i < unanalyzedDocs.length; i++) {
      try {
        await analyzeMutation.mutateAsync(unanalyzedDocs[i].id);
        successCount++;
        setAnalysisProgress({ current: i + 1, total: unanalyzedDocs.length });
      } catch (error) {
        console.error(`Failed to analyze ${unanalyzedDocs[i].filename}`, error);
      }
    }

    setIsAnalyzingDocs(false);
    setAnalysisWarningOpen(false);

    if (successCount > 0) {
      toast.success(`Analyzed ${successCount} of ${unanalyzedDocs.length} documents`);
    }

    // Now generate contract with force_generate=true
    submitContractGeneration(true);
  };

  // Fetch signatures for contract
  const { data: signatures, refetch: refetchSignatures, isLoading: isLoadingSignatures, isFetching: isFetchingSignatures } = useQuery({
    queryKey: ['contract-signatures', contract?._id],
    queryFn: () => contract?._id ? api.get(`/signatures/contract/${contract._id}`) : null,
    enabled: !!contract?._id
  });

  // Sign contract mutation
  const signContractMutation = useMutation({
    mutationFn: (signatureData) => api.post('/signatures/sign', {
      contract_id: contract._id,
      deal_id: dealId,
      ...signatureData
    }),
    onSuccess: () => {
      queryClient.invalidateQueries(['contract-signatures', contract?._id]);
      queryClient.invalidateQueries(['deal-timeline', dealId]);
      queryClient.invalidateQueries(['deal', dealId]);
      queryClient.invalidateQueries(['deal-status-info', dealId]);
      setSignatureDialogOpen(false);
      setSigningContract(false);
      toast.success(t('dealRoom.signature.success') || 'Contract signed successfully!');
    },
    onError: (error) => {
      setSigningContract(false);
      const detail = error.response?.data?.detail || 'Failed to sign contract';
      toast.error(detail);
    }
  });

  // Handle signature submission
  const handleSignContract = async (signatureData) => {
    setSigningContract(true);
    try {
      // 1. Upload signature image to S3 using specific signature presigned endpoint
      const { fileKey } = await api.uploadSignatureViaPresigned(signatureData.signature_data, {
        contractId: contract?._id
      });

      // 2. Submit signature metadata + storage path
      const payload = {
        ...signatureData,
        signature_data: null, // Do not send base64 string to MongoDB
        storage_path: fileKey
      };

      signContractMutation.mutate(payload);
    } catch (e) {
      console.error('Signature upload failed:', e);
      setSigningContract(false);
      const detail = e.response?.data?.detail || e.message || 'Failed to upload signature';
      toast.error(detail);
    }
  };

  // Check if user has already signed
  const hasUserSigned = signatures?.signatures?.some(
    sig => sig.signer_user_id === user?._id
  );

  // Check if all required participants have signed (deal owner + all deal clients)
  const allSigned = (() => {
    const sigs = signatures?.signatures ?? [];
    const ownerSigned = sigs.some(
      s => String(s.signer_user_id) === String(deal?.owner_user_id)
    );
    const clientList = clients ?? [];
    const everyClientSigned = clientList.length === 0 || clientList.every((client) => {
      const clientId = String(client._id);
      const linkedUserId = client.linked_user_id ? String(client.linked_user_id) : null;
      return sigs.some(
        s =>
          String(s.signer_client_id) === clientId ||
          (linkedUserId && String(s.signer_user_id) === linkedUserId)
      );
    });
    return !!deal?.owner_user_id && ownerSigned && everyClientSigned;
  })();

  // Update (save edited) contract mutation
  const updateContractMutation = useMutation({
    mutationFn: (content) => api.put(`/deals/${dealId}/contracts`, { content }),
    onSuccess: () => {
      queryClient.invalidateQueries(['deal-contract', dealId]);
      queryClient.invalidateQueries(['deal-timeline', dealId]);
      setIsEditingContract(false);
      setEditedContractContent({});
      toast.success('Contract saved successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to save contract. Please try again.');
    }
  });

  // Manual contract creation mutation (Basic plan — no AI)
  const createManualContractMutation = useMutation({
    mutationFn: (languages) => api.post(`/deals/${dealId}/contracts/manual`, {
      languages,
      contract_type: 'purchase_agreement',
    }),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['deal-contract', dealId]);
      setManualContractDialogOpen(false);
      // Pre-populate the editor with empty strings for each language, then open edit mode
      const emptyContent = Object.fromEntries((data.languages || ['en']).map(l => [l, '']));
      setEditedContractContent(emptyContent);
      setSelectedContractLanguage(data.languages?.[0] || 'en');
      setIsEditingContract(true);
      toast.success('Empty contract created. Start writing your contract below.');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to create contract. Please try again.');
    },
  });

  // Finalize contract mutation
  const finalizeContractMutation = useMutation({
    mutationFn: () => api.post(`/deals/${dealId}/contracts/finalize`),
    onSuccess: () => {
      queryClient.invalidateQueries(['deal-contract', dealId]);
      queryClient.invalidateQueries(['deal', dealId]);
      queryClient.invalidateQueries(['deal-timeline', dealId]);
      setFinalizeConfirmOpen(false);
      toast.success('Contract finalized and sent for signature!');
    },
    onError: (error) => {
      setFinalizeConfirmOpen(false);
      toast.error(error.response?.data?.detail || 'Failed to finalize contract. Please try again.');
    }
  });

  // Enter edit mode — pre-populate textarea with current contract content
  const handleStartEdit = () => {
    setEditedContractContent({ ...(contract?.content || {}) });
    setIsEditingContract(true);
  };

  // Save edited contract
  const handleSaveEdit = () => {
    updateContractMutation.mutate(editedContractContent);
  };

  // Cancel editing — discard changes
  const handleCancelEdit = () => {
    setIsEditingContract(false);
    setEditedContractContent({});
  };

  // Finalize contract (after confirmation)
  const handleFinalizeContract = () => {
    finalizeContractMutation.mutate();
  };

  // Download finalized contract as PDF
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const handlePrintContract = () => window.print();

  const handleDownloadPdf = async () => {
    if (!contract?._id || !dealId) return;
    setIsDownloadingPdf(true);
    try {
      const blob = await api.getBlob(
        `/deals/${dealId}/contracts/download?lang=${selectedContractLanguage}`
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contract_${dealId}_${selectedContractLanguage}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Contract PDF downloaded successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to download PDF. Please try again.');
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  // Copy contract content to clipboard
  const copyContractContent = (language) => {
    if (contract?.content?.[language]) {
      navigator.clipboard.writeText(contract.content[language]);
      toast.success(`${language.toUpperCase()} contract copied to clipboard`);
    }
  };

  // Send message
  const sendMessageMutation = useMutation({
    mutationFn: (text) => api.post(`/conversations/${conversation._id}/messages`, { text }),
    onSuccess: () => {
      setMessageInput('');
      refetchMessages();
    },
    onError: () => {
      toast.error('Failed to send message');
    }
  });

  // Suggest reply
  const suggestReplyMutation = useMutation({
    mutationFn: () => api.post(`/conversations/${conversation._id}/suggest-reply`, {}),
    onSuccess: (data) => {
      setMessageInput(data.suggested_text || '');
      setSuggestingReply(false);
    },
    onError: (error) => {
      setSuggestingReply(false);
      if (error?.status === 402 || error?.response?.status === 402) {
        setUpgradeDialogOpen(true);
      } else {
        toast.error('Failed to generate suggestion');
      }
    }
  });

  // Upload document (S3 presigned for Deal Room) — retry up to 3 times on failure
  const uploadMutation = useMutation({
    mutationFn: async (file) => {
      const maxAttempts = 4; // 1 initial + 3 retries
      let lastError;
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          return await api.uploadViaPresigned(file, { dealId });
        } catch (err) {
          lastError = err;
          if (attempt === maxAttempts) throw lastError;
        }
      }
      throw lastError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['deal-documents', dealId]);
      queryClient.invalidateQueries(['deal-timeline', dealId]);
      toast.success('Document uploaded');
    },
    onError: () => {
      toast.error('Upload failed');
    }
  });

  // Analyze document
  const analyzeMutation = useMutation({
    mutationFn: (docId) => api.post(`/documents/${docId}/analyze`),
    onSuccess: (data, docId) => {
      queryClient.invalidateQueries(['deal-documents', dealId]);
      queryClient.invalidateQueries(['deal-timeline', dealId]);
      toast.success('AI document analysis complete');
      // Update selected document if it's the one being analyzed
      if (selectedDocument?._id === docId) {
        setSelectedDocument(prev => ({
          ...prev,
          ai_extracted_data: data.analysis,
          status: 'processed'
        }));
      }
    },
    onError: () => {
      toast.error('AI analysis failed');
    }
  });

  // Delete document
  // Delete document with Optimistic Update
  const deleteMutation = useMutation({
    mutationFn: (docId) => api.delete(`/documents/${docId}`),
    onMutate: async (docId) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries(['deal-documents', dealId]);

      // Snapshot the previous value
      const previousDocuments = queryClient.getQueryData(['deal-documents', dealId]);

      // Optimistically update to remove the item
      queryClient.setQueryData(['deal-documents', dealId], (old) => {
        return old ? old.filter(d => d._id !== docId) : [];
      });

      // Close sheet immediately for better UX
      setDocumentSheetOpen(false);
      setSelectedDocument(null);

      // Return context object with the snapshotted value
      return { previousDocuments };
    },
    onError: (err, newTodo, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      queryClient.setQueryData(['deal-documents', dealId], context.previousDocuments);
      toast.error('Failed to delete document');
      // Re-open sheet if it was open (optional, but good for UX)
    },
    onSettled: () => {
      // Always refetch after error or success:
      queryClient.invalidateQueries(['deal-documents', dealId]);
      queryClient.invalidateQueries(['deal-timeline', dealId]);
    },
    onSuccess: () => {
      toast.success('Document deleted');
    }
  });

  const handleSendMessage = () => {
    if (!messageInput.trim() || !conversation?._id) return;
    sendMessageMutation.mutate(messageInput.trim());
  };

  const handleSuggestReply = () => {
    if (!conversation?._id) return;
    setSuggestingReply(true);
    suggestReplyMutation.mutate();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setIsUploading(true);
      setUploadProgress(0);
      // Simulate progress
      const interval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(interval);
            return 90;
          }
          return prev + 10;
        });
      }, 100);

      uploadMutation.mutate(file, {
        onSettled: () => {
          clearInterval(interval);
          setUploadProgress(100);
          setTimeout(() => {
            setIsUploading(false);
            setUploadProgress(0);
            if (fileInputRef.current) fileInputRef.current.value = '';
          }, 500);
        }
      });
    }
  };

  const handleDocumentClick = (doc) => {
    setSelectedDocument(doc);
    setDocumentSheetOpen(true);
  };

  const handlePreviewClick = (doc) => {
    const isImage = doc?.mime_type?.startsWith('image/');
    const isPdf = doc?.mime_type === 'application/pdf';

    if (isImage || isPdf) {
      setFullScreenPreviewOpen(true);
    } else {
      toast.info(t ? t('dealRoom.documents.previewNotAvailable') : 'Preview not available. Downloading...');
      handleDownload(doc);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const isS3 = doc?.storage_path?.startsWith?.('uploads/');

      // Try S3 first (most likely for new docs)
      try {
        if (isS3) { // If it STARTS with 'uploads/', it is S3
          // Request download=true to get Content-Disposition attachment
          const { url } = await api.get(`/documents/${doc._id}/presigned-url?download=true`);

          // Create temp link to force download
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', doc.original_filename); // Hint to browser
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          return;
        }
        // Fallback for local
        throw new Error("Not S3");
        throw new Error("Not S3");
      } catch (err) {
        // Fallback or Local file
        if (err.response?.status === 400 || err.message === "Not S3") {
          // It's a local file.
          const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
          const fileUrl = `${backendUrl}/uploads/${doc.storage_path}`;
          window.open(fileUrl, '_blank'); // Local files might still open in new tab, but S3 is priority.
        } else {
          throw err;
        }
      }
    } catch (err) {
      toast.error(t ? t('common.error') : 'Failed to download file');
    }
  };

  const handleDeleteDocument = (docId) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      deleteMutation.mutate(docId);
    }
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith('image/')) return Image;
    if (mimeType?.includes('pdf')) return FileText;
    return File;
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return 'text-green-600 dark:text-green-400';
    if (confidence >= 0.5) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const toggleMessageExpand = (msgId) => {
    setExpandedMessages(prev => ({ ...prev, [msgId]: !prev[msgId] }));
  };

  if (dealLoading) {
    return (
      <div className="space-y-6" data-testid="deal-room-loading">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!deal) {
    return (
      <div className="text-center py-12" data-testid="deal-not-found">
        <p className="text-muted-foreground">{t('dealRoom.notFound')}</p>
        <Button variant="outline" onClick={() => navigate('/deals')} className="mt-4">
          {t('dealRoom.back')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in pb-10" data-testid="deal-room-page">
      {/* Header - Per spec Section 5.2 */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        <div className="flex items-center gap-3 sm:gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/deals')}
            aria-label="Back to deals"
            data-testid="back-button"
            className="min-h-[48px] min-w-[48px]"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <SvgIcon src="briefcase.svg" className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-2xl font-display font-bold truncate" data-testid="deal-title">{deal.title}</h1>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-1">
                <span className="text-xs sm:text-sm text-muted-foreground capitalize">{deal.type?.replace('_', ' ')}</span>
                <StatusBadge status={deal.status} />
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 hidden sm:block" />

        {/* Schedule button */}
        {user?.organization_id === deal?.organization_id && (
          <Button
            variant="outline"
            size="default"
            className="hidden sm:flex min-h-[48px] gap-2"
            onClick={() => setScheduleModalOpen(true)}
          >
            <Calendar className="h-4 w-4" />
            {t('schedule.button') || 'Schedule'}
          </Button>
        )}

        {/* Status dropdown with allowed transitions - Flexible Workflow */}
        <div className="w-full sm:w-auto">
          {user?.organization_id === deal?.organization_id ? (
            <Select
              value={deal.status}
              onValueChange={(value) => {
                if (value === deal.status) return;
                const targetStatus = statusInfo?.allowed_transitions?.find(t => t.status === value);
                if (targetStatus) {
                  handleStatusChangeClick(targetStatus);
                }
              }}
            >
              <SelectTrigger className="w-full sm:w-48 min-h-[48px]" data-testid="deal-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {/* Include current status in options when not in allowed_transitions (e.g. pending_payment) so Select value matches */}
                {(() => {
                  const allowed = statusInfo?.allowed_transitions ?? [];
                  const current = deal?.status;
                  const currentInAllowed = allowed.some(t => t.status === current);
                  const options = currentInAllowed
                    ? allowed
                    : [
                      {
                        status: current,
                        label: statusInfo?.status_info?.label || (current && current.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())),
                        description: statusInfo?.status_info?.description
                      },
                      ...allowed
                    ];
                  return options.map(transition => {
                    // Conditional Logic for Disabling
                    let isDisabled = false;
                    let disabledReason = "";

                    // 1. Disable Pending Signature if contract not finalized
                    if (transition.status === 'pending_signature' && contract?.status !== 'finalized') {
                      isDisabled = true;
                      disabledReason = "Contract not finalized";
                    }

                    // 2. Disable Pending Payment unless all participants have signed (same idea as Pending Signature)
                    if (transition.status === 'pending_payment' && deal.status !== 'pending_payment' && !allSigned) {
                      isDisabled = true;
                      disabledReason = "All participants must sign first";
                    }

                    // 3. Disable reverting from Pending Signature to earlier stages
                    if (deal.status === 'pending_signature' && ['pending_documents', 'draft', 'active', 'on_hold'].includes(transition.status)) {
                      isDisabled = true;
                      disabledReason = t("dealroom.cannotRevert") || "Cannot revert from Pending Signature";
                    }

                    // 4. Disable reverting from Pending Payment to earlier stages (same as Pending Signature)
                    if (deal.status === 'pending_payment' && ['pending_documents', 'draft', 'active', 'on_hold', 'pending_signature'].includes(transition.status)) {
                      isDisabled = true;
                      disabledReason = "Cannot revert from Pending Payment";
                    }

                    // Current status is display-only (disabled)
                    const isCurrent = transition.status === deal.status;

                    return (
                      <SelectItem
                        key={transition.status}
                        value={transition.status}
                        disabled={isDisabled || isCurrent}
                        className={isDisabled || isCurrent ? "opacity-50 cursor-not-allowed" : ""}
                      >
                        <div className="flex items-center gap-2">
                          {(() => {
                            const TransitionIcon = statusIcons[transition.status] || FileText;
                            return <TransitionIcon className="h-4 w-4" />;
                          })()}
                          <span>{transition.label}</span>
                          {isCurrent && <span className="text-xs text-muted-foreground ml-2">(current)</span>}
                          {isDisabled && !isCurrent && disabledReason && (
                            <span className="text-xs text-muted-foreground ml-2">({disabledReason})</span>
                          )}
                        </div>
                      </SelectItem>
                    );
                  });
                })()}
              </SelectContent>
            </Select>
          ) : (
            // Read-only view for Clients
            <Badge
              variant="outline"
              className={`${statusColors[deal.status]} px-3 py-1.5 text-sm h-[48px] flex items-center justify-center min-w-[120px]`}
            >
              {(() => {
                const StatusIcon = statusIcons[deal.status] || FileText;
                return <StatusIcon className="h-4 w-4 mr-1.5" />;
              })()}
              {deal.status?.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
            </Badge>
          )}
        </div>
      </div>



      {/* Read-only Alert for closed/cancelled/completed deals */}
      {isReadOnly(deal.status) && (
        <Alert className={
          deal.status === 'cancelled'
            ? 'border-destructive/50 bg-destructive/5'
            : deal.status === 'completed'
              ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-950/20'
              : 'border-muted'
        }>
          <Lock className={`h-4 w-4 ${deal.status === 'completed' ? 'text-emerald-600' : ''}`} />
          <AlertDescription className={deal.status === 'completed' ? 'text-emerald-700 dark:text-emerald-400' : ''}>
            {deal.status === 'closed'
              ? t('dealRoom.readOnly.closedMessage')
              : deal.status === 'completed'
                ? t('dealRoom.readOnly.completedMessage')
                : t('dealRoom.readOnly.cancelledMessage')}
          </AlertDescription>
        </Alert>
      )}

      {/* On Hold Alert */}
      {isOnHold(deal.status) && (
        <Alert className="border-orange-300 bg-orange-50 dark:bg-orange-950/20">
          <Pause className="h-4 w-4 text-orange-600" />
          <AlertDescription className="text-orange-700 dark:text-orange-400">
            {t('dealRoom.onHold.message')}
          </AlertDescription>
        </Alert>
      )}

      {/* Pending Signature Alert (or Pending Payment when all signed) */}
      {(isPendingSignature(deal.status) || deal.status === 'pending_payment') && (
        <>
          {(deal.status === 'pending_payment' || (isPendingSignature(deal.status) && allSigned)) ? (
            <Alert className="border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-800">
              <AlertDescription className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0 sm:justify-between">
                <span className="flex items-center gap-2 text-emerald-800 dark:text-emerald-200">
                  <CheckCircle className="h-5 w-5 shrink-0 text-emerald-600" />
                  {dealInvoices.length === 0
                    ? (t('dealRoom.signedByAll.message') || 'Signed by all participants. Create an invoice to proceed with payment.')
                    : `${dealInvoices.length} invoice${dealInvoices.length !== 1 ? 's' : ''} created. Awaiting payment.`
                  }
                </span>
                <div className="flex items-end gap-2 flex-shrink-0 self-end sm:self-auto sm:ml-auto">
                  {/* Deal creator: show Create Invoice if no invoices yet */}
                  {canCreateInvoice && dealInvoices.length === 0 && (
                    <Button
                      size="sm"
                      className="min-h-9 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => setInvoiceModalOpen(true)}
                      data-testid="create-invoice-button"
                    >
                      <DollarSign className="h-4 w-4 mr-1.5" />
                      Create Invoice
                    </Button>
                  )}
                  {/* View existing invoices if created */}
                  {dealInvoices.length > 0 && (
                    <Badge variant="outline" className="border-emerald-500 text-emerald-700 bg-emerald-50">
                      {dealInvoices.filter(i => i.escrow_status === 'funded').length} / {dealInvoices.length} funded
                    </Badge>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="min-h-9 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
                    onClick={() => setSignaturesListOpen(true)}
                    data-testid="signatures-list-button"
                  >
                    <FileSignature className="h-4 w-4" />
                    {t('dealRoom.signaturesList.viewSignatures') || 'View signatures'}
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          ) : (
            <Alert className="border-purple-300 bg-purple-50 dark:bg-purple-950/20">
              <AlertDescription className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0 sm:justify-between">
                <span className="flex items-center gap-2 text-purple-700 dark:text-purple-400">
                  <FileSignature className="h-5 w-5 shrink-0 text-purple-600" />
                  {(isLoadingSignatures || isFetchingSignatures || signingContract)
                    ? t('dealRoom.pendingSignature.loading', 'Loading signature status...')
                    : hasUserSigned
                      ? (t('dealRoom.pendingSignature.signedWaitingOthers') || 'Signed successfully. Waiting for other participants.')
                      : t('dealRoom.pendingSignature.message')}
                </span>
                {(isLoadingSignatures || isFetchingSignatures || signingContract) && (
                  <div className="flex items-center ml-auto pr-4">
                    <Loader2 className="h-5 w-5 animate-spin text-purple-600" />
                  </div>
                )}
                {!(isLoadingSignatures || isFetchingSignatures || signingContract) && contract && !hasUserSigned && (
                  <div className="flex items-end gap-2 flex-shrink-0 self-start sm:self-auto sm:ml-auto mt-2 sm:mt-0">
                    <Button
                      size="sm"
                      variant="default"
                      className="flex bg-purple-600 hover:bg-purple-700 min-h-[44px]"
                      onClick={() => setSignatureDialogOpen(true)}
                      data-testid="sign-contract-button"
                    >
                      <FileSignature className="h-4 w-4 mr-2" />
                      Sign Now
                    </Button>
                  </div>
                )}
                {!(isLoadingSignatures || isFetchingSignatures || signingContract) && hasUserSigned && (
                  <div className="flex  items-end gap-2 flex-shrink-0 self-end sm:self-auto sm:ml-auto">
                    <Badge
                      variant="secondary"
                      className="inline-flex items-center justify-center gap-2 min-h-9 h-9 px-4 rounded-md text-xs font-medium bg-emerald-500/15 text-emerald-800 dark:bg-emerald-400/20 dark:text-emerald-200 border border-emerald-500/30 cursor-default"
                      data-testid="signed-by-you-badge"
                    >
                      <Check className="h-4 w-4 shrink-0" />
                      {t('dealRoom.signature.signedByYou') || 'Signed by you'}
                    </Badge>
                    <Button
                      size="sm"
                      variant="outline"
                      className="min-h-9 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
                      onClick={() => setSignaturesListOpen(true)}
                      data-testid="signatures-list-button"
                    >
                      <FileSignature className="h-4 w-4" />
                      {t('dealRoom.signaturesList.viewSignatures') || 'View signatures'}
                    </Button>
                  </div>
                )}
              </AlertDescription>
            </Alert>
          )}
        </>
      )}

      {/* Payment Status Panel — escrow fund/release/dispute actions */}
      {['pending_payment', 'escrow_funded', 'release_requested', 'disputed', 'completed'].includes(deal.status) && dealInvoices.length > 0 && (
        <PaymentStatusPanel
          invoices={dealInvoices}
          currentUser={user}
          deal={deal}
          clients={clients}
          onRefresh={() => {
            queryClient.invalidateQueries(['deal-invoices', dealId]);
            queryClient.invalidateQueries(['deal', dealId]);
          }}
        />
      )}

      {/* AI Summary Card - Per spec Section 8 (auto-generated; no manual refresh) */}
      <Card className="bg-accent/5 border-accent/20">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <SvgIcon src="bulb.svg" className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-accent">{t('dealRoom.aiSummary.title')}</span>
              </div>
              {summaryLoading ? (
                <Skeleton className="h-12 w-full" />
              ) : (
                <p className="text-sm text-muted-foreground" data-testid="deal-ai-summary">
                  {aiSummary?.summary || t('dealRoom.aiSummary.generating')}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Content Grid - Stack on mobile; fixed height on lg so right column scrolls instead of expanding */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:h-[720px] mb-6">
        {/* Left Column - Timeline (Stack on mobile) */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <SvgIcon src="clock.svg" className="h-5 w-5" />
                {t('dealRoom.timeline.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[400px] p-6">
                {timeline && timeline.length > 0 ? (
                  <div className="relative">
                    <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-border" />
                    <div className="space-y-4">
                      {timeline.map((event, idx) => (
                        <div key={idx} className="relative pl-8" data-testid="timeline-event">
                          <div className="absolute left-0 w-6 h-6 rounded-full bg-muted flex items-center justify-center -ml-[1px]">
                            <SvgIcon
                              src={timelineIcons[event.type] || timelineIcons.default}
                              className="h-3.5 w-3.5"
                            />
                          </div>
                          <div>
                            <p className="font-medium text-sm">
                              {event.title}
                              {event.type !== 'signature_added' && event.created_by_name && (
                                <span className="ml-2 text-xs font-normal text-muted-foreground">
                                  {t('dealRoom.timeline.createdBy', { name: event.created_by_name })}
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-muted-foreground">{event.description}</p>
                            <time className="text-xs text-muted-foreground">
                              {formatTimelineTimestamp(event.timestamp)}
                            </time>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    {t('dealRoom.timeline.empty')}
                  </p>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Upcoming scheduled (calls/recordings) */}
          {orgPlanInfo?.plan === 'pro' && scheduledEvents.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  {t('schedule.upcoming') || 'Upcoming scheduled'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {scheduledEvents.map((ev) => {
                  const at = ev.scheduled_at ? new Date(ev.scheduled_at) : null;
                  const atStr = at ? at.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : '';
                  return (
                    <div key={ev._id} className="flex items-center justify-between gap-2 p-2 rounded-lg border bg-muted/30">
                      <div className="min-w-0">
                        <p className="text-sm font-medium capitalize">{ev.type}</p>
                        <p className="text-xs text-muted-foreground">{atStr}</p>
                      </div>
                      {user?.organization_id === deal?.organization_id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => cancelScheduleMutation.mutate(ev._id)}
                          disabled={cancelScheduleMutation.isPending}
                        >
                          {t('schedule.cancel') || 'Cancel'}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {/* Deal Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <SvgIcon src="filter.svg" className="h-5 w-5" />
                {t('dealRoom.details.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-xs text-muted-foreground">{t('dealRoom.details.notes')}</label>
                <Textarea
                  value={deal.summary || ''}
                  onChange={(e) => updateDealMutation.mutate({ summary: e.target.value })}
                  placeholder={t('dealRoom.details.addNotes')}
                  rows={3}
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">{t('dealRoom.details.type')}</p>
                  <p className="font-medium capitalize">{deal.type?.replace('_', ' ')}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('dealRoom.details.messages')}</p>
                  <p className="font-medium">{messages?.length || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('dealRoom.details.documents')}</p>
                  <p className="font-medium">{documents?.length || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('dealRoom.details.created')}</p>
                  <p className="font-medium text-xs">{formatDealRoomDate(deal.created_at)}</p>
                </div>

                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground">{t('dealRoom.details.clients') || 'Clients'}</p>
                  <p className="font-medium truncate" title={clients?.map(c => c.name).join(', ')}>
                    {clients && clients.length > 0
                      ? clients.map(c => c.name || 'Unnamed Client').join(', ')
                      : t('common.none')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Messages, Documents & Contract - overflow-hidden so row height follows left column; inner scroll works */}
        <div className="lg:col-span-2 flex flex-col min-h-0 overflow-hidden">
          <Tabs defaultValue="messages" data-testid="deal-room-tabs" className="flex flex-col flex-1 min-h-0 overflow-hidden">
            {/* Scrollable tab header on small screens */}
            <div className="overflow-x-auto scrollbar-hide -mx-1 px-1">
              <TabsList className="inline-flex w-max min-w-full h-9">
                <TabsTrigger value="messages" className="flex items-center gap-2 whitespace-nowrap" data-testid="messages-tab">
                  <SvgIcon src="chat.svg" className="h-4 w-4" />
                  {t('dealRoom.tabs.messages')}
                </TabsTrigger>
                <TabsTrigger value="documents" className="flex items-center gap-2 whitespace-nowrap" data-testid="documents-tab">
                  <SvgIcon src="clipboard.svg" className="h-4 w-4" />
                  {t('dealRoom.tabs.documents')} ({documents?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="contract" className="flex items-center gap-2 whitespace-nowrap" data-testid="contract-tab">
                  <FileSignature className="h-4 w-4" />
                  {t('dealRoom.tabs.contract')} {contract ? '(1)' : ''}
                </TabsTrigger>
                <TabsTrigger value="meetings" className="flex items-center gap-2 whitespace-nowrap" data-testid="meetings-tab">
                  Meetings {dealMeetings.length > 0 && <span className="text-xs bg-primary/10 text-primary rounded-full px-1.5">{dealMeetings.length}</span>}
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Messages Tab */}
            <TabsContent value="messages" className="mt-4 flex-1 flex flex-col min-h-0 overflow-hidden data-[state=inactive]:hidden">
              <Card className="flex flex-col flex-1 min-h-0 overflow-hidden">
                <CardHeader className="flex-shrink-0 border-b py-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{t('dealRoom.conversation.title')}</CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => refetchMessages()}
                      aria-label={t('common.refresh')}
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>

                {/* Active Call Persistent Banner */}

                <div
                  ref={scrollContainerRef}
                  className="flex-1 p-4 overflow-y-auto overflow-x-hidden min-h-0"
                  data-testid="message-list"
                >
                  {wsError ? (
                    <div className="flex flex-col items-center justify-center h-full text-center py-8 space-y-3">
                      <AlertCircle className="h-10 w-10 text-destructive/50" />
                      <p className="text-sm font-medium text-muted-foreground">{t('dealRoom.messages.connectionError')}</p>
                      <Button variant="outline" size="sm" onClick={() => window.location.reload()} className="mt-2">
                        {t('common.refresh')}
                      </Button>
                    </div>
                  ) : (messagesLoading || !isConnected) ? (
                    <div className="flex flex-col items-center justify-center h-full text-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-primary/50 mb-3" />
                      <p className="text-sm text-muted-foreground">{t('dealRoom.messages.connecting')}</p>
                    </div>
                  ) : messages && messages.length > 0 ? (
                    <div className="space-y-4 w-full overflow-x-hidden">
                      {messages.map((msg) => {
                        const isUser = msg.from?.type === 'user';
                        // STRICT CHECK: Is this message from the currently logged-in user?
                        const isMe = isUser && msg.from?.id === user?._id;

                        const isExpanded = expandedMessages[msg._id];
                        const hasTranslation = msg.translated_text && msg.translated_text !== msg.original_text;

                        // Use enriched sender name from backend, or fallback
                        let senderName = msg.sender_name || '';

                        // Fallback logic if backend didn't provide name (e.g. older messages)
                        if (!senderName) {
                          if (isMe) {
                            senderName = user?.name || t('dealRoom.conversation.you');
                          } else if (isUser) {
                            senderName = t('dealRoom.conversation.colleague'); // Generic fallback for other users
                          } else {
                            // Try to find client
                            const client = clients?.find(c => c._id === msg.from?.id);
                            senderName = client?.name || t('dealRoom.conversation.client');
                          }
                        }

                        // Modify senderName for "Me" to be "You" in UI
                        const displayName = isMe ? t('dealRoom.conversation.you') : senderName;

                        const senderInitials = senderName
                          ?.split(' ')
                          .map(n => n[0])
                          .join('')
                          .substring(0, 2)
                          .toUpperCase() || '??';

                        // --- Custom View for Call Started System Message ---
                        if (msg.type === 'call_started') {
                            return (
                                <div key={msg._id} className="w-full flex justify-center my-4" data-testid={`message-${msg._id}`}>
                                    <div className="bg-muted/50 border border-border px-4 py-2 rounded-lg max-w-[90%] sm:max-w-md flex flex-col sm:flex-row items-center gap-3 shadow-sm select-none">
                                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                           <Video className="h-4 w-4 text-primary" />
                                        </div>
                                        <div className="flex flex-col flex-1 min-w-0 text-center sm:text-left">
                                            <p className="text-sm font-medium text-foreground truncate">
                                                {msg.sender_name} joined a video call
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                {formatDealRoomTime(msg.sent_at || msg.created_at)}
                                            </p>
                                        </div>

                                    </div>
                                </div>
                            );
                        }

                        // --- Custom View for Call Ended System Message ---
                        if (msg.type === 'call_ended') {
                            return (
                                <div key={msg._id} className="w-full flex justify-center my-2" data-testid={`message-${msg._id}`}>
                                    <div className="bg-transparent px-4 py-1.5 rounded-full flex items-center justify-center select-none text-muted-foreground/80">
                                        <div className="flex items-center gap-2 text-xs">
                                           <span>{msg.sender_name} left the call</span>
                                           <span>•</span>
                                           <span>{formatDealRoomTime(msg.sent_at || msg.created_at)}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        }

                        return (
                          <div
                            key={msg._id}
                            className={`flex items-end gap-2 w-full min-w-0 ${isMe ? 'justify-end' : 'justify-start'}`}
                            data-testid={`message-${msg._id}`}
                          >
                            {/* Avatar for others (Left side) */}
                            {!isMe && (
                              <Avatar className="h-8 w-8 flex-shrink-0">
                                <AvatarFallback className="bg-primary/10 text-primary text-xs">
                                  {senderInitials}
                                </AvatarFallback>
                              </Avatar>
                            )}

                            <div className={`min-w-0 flex flex-col ${isMe ? 'w-fit max-w-[80%] items-end' : 'max-w-[80%]'}`}>
                              <div className={`flex items-center gap-2 mb-1 px-1 ${isMe ? 'justify-end' : 'justify-start'}`}>
                                <span className="text-xs text-muted-foreground">
                                  {displayName}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {formatDealRoomTime(msg.sent_at || msg.created_at)}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => handleReadAloudMessage(msg)}
                                  disabled={ttsPlayingMsgId === msg._id || !(msg.original_text || msg.translated_text)}
                                  className="p-0.5 rounded hover:bg-muted transition-colors disabled:opacity-50"
                                  title={t('messages.readAloud.title') || 'Read aloud'}
                                  aria-label={t('messages.readAloud.title') || 'Read aloud'}
                                >
                                  {ttsPlayingMsgId === msg._id ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <Volume2 className="h-3.5 w-3.5" />
                                  )}
                                </button>
                              </div>
                              <div className={`rounded-lg p-3 shadow-sm max-w-full ${isMe
                                ? 'bg-primary text-primary-foreground rounded-br-none text-right w-fit'
                                : 'bg-muted rounded-bl-none w-fit'
                                }`}>
                                <p className="text-sm whitespace-pre-wrap break-words">
                                  {isMe ? msg.original_text : (msg.translated_text || msg.original_text)}
                                </p>
                                {hasTranslation && !isMe && (
                                  <button
                                    onClick={() => toggleMessageExpand(msg._id)}
                                    className="text-xs mt-2 flex items-center gap-1 opacity-70 hover:opacity-100"
                                  >
                                    <Globe className="h-3 w-3" />
                                    {isExpanded ? t('dealRoom.messages.hideOriginal') : t('dealRoom.messages.showOriginal')}
                                    <ChevronDown className={`h-3 w-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                  </button>
                                )}
                                {isExpanded && hasTranslation && (
                                  <p className="text-xs mt-2 pt-2 border-t border-current/20 opacity-70 break-words">
                                    {t('dealRoom.messages.original', { language: msg.original_language?.toUpperCase() })}: {msg.original_text}
                                  </p>
                                )}
                              </div>
                            </div>

                            {/* Avatar for user (Right side) */}
                            {isMe && (
                              <Avatar className="h-8 w-8 flex-shrink-0">
                                <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                                  {senderInitials}
                                </AvatarFallback>
                              </Avatar>
                            )}
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-center py-8">
                      <SvgIcon src="discussion.svg" className="h-16 w-16 opacity-50 mb-4" />
                      <p className="text-sm text-muted-foreground">{t('dealRoom.messages.empty')}</p>
                    </div>
                  )}
                </div>

                {/* Message Input - Hidden for read-only states */}
                {canSendMessages(statusInfo?.status_info) ? (
                  <div className="p-3 sm:p-4 border-t space-y-2">
                    {/* Input Container */}
                    <div className="relative flex gap-2">
                      <Textarea
                        value={messageInput}
                        onChange={(e) => {
                          setMessageInput(e.target.value);
                        }}
                        placeholder={t('dealRoom.messages.placeholder')}
                        className="min-h-[48px] sm:min-h-[60px] max-h-[120px] resize-none text-base"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                        data-testid="message-input"
                      />
                      {/* Typing Indicator */}
                      {Object.keys(typingUsers).length > 0 && (
                        <div className="absolute bottom-full left-0 mb-4 px-1 z-50 pointer-events-none">
                          <p className="text-xs text-muted-foreground animate-pulse font-medium bg-background/80 backdrop-blur-sm px-2 py-1 rounded-md shadow-sm">
                            {Object.values(typingUsers).join(', ')} {Object.keys(typingUsers).length === 1 ? 'is' : 'are'} typing...
                          </p>
                        </div>
                      )}
                      {/* Mobile: Show only send icon */}
                      <Button
                        onClick={handleSendMessage}
                        disabled={!messageInput.trim() || sendMessageMutation.isPending || !conversation?._id}
                        aria-label={t('dealRoom.messages.send')}
                        data-testid="send-message-button"
                        className="sm:hidden min-h-[48px] min-w-[48px] px-3"
                        size="icon"
                      >
                        {sendMessageMutation.isPending ? (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                          <Send className="h-5 w-5" />
                        )}
                      </Button>
                    </div>
                    <div className="flex justify-between items-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={orgPlanInfo?.plan !== 'pro' ? () => setUpgradeDialogOpen(true) : handleSuggestReply}
                        disabled={suggestingReply || !conversation?._id}
                        data-testid="suggest-reply-button"
                        className="min-h-[44px]"
                        title={orgPlanInfo?.plan !== 'pro' && orgPlanInfo?.plan !== 'starter' && orgPlanInfo?.plan !== 'growth' && orgPlanInfo?.plan !== 'scale' && orgPlanInfo?.plan !== 'enterprise' ? 'AI suggested replies require the Small Business plan or higher' : undefined}
                      >
                        {suggestingReply ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <SvgIcon src="bulb.svg" className="h-4 w-4 mr-1" />
                        )}
                        <span className="hidden sm:inline">
                          {orgPlanInfo?.plan !== 'pro' ? `${t('dealRoom.messages.aiSuggest')} (Pro)` : t('dealRoom.messages.aiSuggest')}
                        </span>
                        <span className="sm:hidden">AI</span>
                      </Button>
                      {/* Desktop: Show full send button */}
                      <Button
                        onClick={handleSendMessage}
                        disabled={!messageInput.trim() || sendMessageMutation.isPending || !conversation?._id}
                        aria-label={t('dealRoom.messages.send')}
                        className="hidden sm:flex min-h-[44px]"
                      >
                        {sendMessageMutation.isPending ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4 mr-1" />
                        )}
                        {t('dealRoom.messages.send')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 border-t bg-muted/30">
                    <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                      <Lock className="h-4 w-4" />
                      <span>{t('dealRoom.messages.disabled')}</span>
                    </div>
                  </div>
                )}
              </Card>
            </TabsContent>

            {/* Documents Tab - Enhanced per spec; fixed height + scroll like Messages */}
            <TabsContent value="documents" className="mt-4 flex-1 flex flex-col min-h-0 overflow-hidden data-[state=inactive]:hidden">
              <Card className="flex flex-col flex-1 min-h-0 overflow-hidden">
                <CardHeader className="flex-shrink-0 border-b py-3">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between min-w-0">
                    <CardTitle className="text-lg truncate">{t('dealRoom.documents.title')}</CardTitle>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2 w-full sm:w-auto min-w-0">
                      {isUploading && (
                        <div className="flex items-center gap-2 w-full sm:w-auto min-w-0">
                          <Progress value={uploadProgress} className="flex-1 min-w-0 h-2 sm:flex-none sm:w-24" />
                          <span className="text-xs text-muted-foreground flex-shrink-0">{uploadProgress}%</span>
                        </div>
                      )}
                      {canUploadDocs(statusInfo?.status_info) ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isUploading}
                          data-testid="upload-document-button"
                          className="w-full sm:w-auto"
                        >
                          {isUploading ? (
                            <Loader2 className="h-4 w-4 mr-1 animate-spin flex-shrink-0" />
                          ) : (
                            <Upload className="h-4 w-4 mr-1 flex-shrink-0" />
                          )}
                          <span className="truncate">{t('dealRoom.documents.upload')}</span>
                        </Button>
                      ) : (
                        <Badge variant="outline" className="text-muted-foreground w-fit">
                          <Lock className="h-3 w-3 mr-1 flex-shrink-0" />
                          <span className="truncate">{t('dealRoom.documents.uploadDisabled')}</span>
                        </Badge>
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      onChange={handleFileSelect}
                      accept="image/*,application/pdf,.pdf,.png,.jpg,.jpeg,.txt,.doc,.docx"
                      capture="environment"
                      data-testid="document-file-input"
                    />
                  </div>
                </CardHeader>
                <CardContent className="flex flex-col flex-1 min-h-0 overflow-hidden p-4">
                  <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 space-y-4">
                    {docsLoading ? (
                      <div className="space-y-2">
                        {[1, 2].map(i => <Skeleton key={i} className="h-20 w-full" />)}
                      </div>
                    ) : documents && documents.length > 0 ? (
                      <div className="space-y-3">
                        {/* Analyzed Documents Section */}
                        {documents.filter(d => d.ai_extracted_data).length > 0 && (
                          <div className="space-y-2">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1 min-w-0">
                              <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                              <span className="whitespace-nowrap">{t('dealRoom.documents.analyzed')} ({documents.filter(d => d.ai_extracted_data).length})</span>
                            </p>
                            {documents.filter(d => d.ai_extracted_data).map(doc => (
                              <DocumentCard
                                key={doc._id}
                                doc={doc}
                                onClick={() => handleDocumentClick(doc)}
                                onAnalyze={() => analyzeMutation.mutate(doc._id)}
                                onDownload={() => handleDownload(doc)}
                                onDelete={() => handleDeleteDocument(doc._id)}
                                onReadAloud={() => handleReadAloudDocument(doc)}
                                ttsPlaying={ttsPlayingDocId === doc._id}
                                isAnalyzing={analyzeMutation.isPending}
                                getFileIcon={getFileIcon}
                                isReadOnly={isReadOnly(deal.status)}
                                canUpload={canUploadDocs(statusInfo?.status_info)}
                                canDelete={user?.organization_id === deal?.organization_id} // Strict: Only Deal Owner can delete
                                t={t}
                              />
                            ))}
                          </div>
                        )}

                        {/* Pending Analysis Section */}
                        {documents.filter(d => !d.ai_extracted_data).length > 0 && (
                          <div className="space-y-2">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1 min-w-0">
                              <AlertCircle className="h-3 w-3 text-yellow-500 flex-shrink-0" />
                              <span className="whitespace-nowrap">{t('dealRoom.documents.pendingAnalysis')} ({documents.filter(d => !d.ai_extracted_data).length})</span>
                            </p>
                            {documents.filter(d => !d.ai_extracted_data).map(doc => (
                              <DocumentCard
                                key={doc._id}
                                doc={doc}
                                onClick={() => handleDocumentClick(doc)}
                                onAnalyze={() => analyzeMutation.mutate(doc._id)}
                                onDownload={() => handleDownload(doc)}
                                onDelete={() => handleDeleteDocument(doc._id)}
                                onReadAloud={() => handleReadAloudDocument(doc)}
                                ttsPlaying={ttsPlayingDocId === doc._id}
                                isAnalyzing={analyzeMutation.isPending}
                                getFileIcon={getFileIcon}
                                isReadOnly={isReadOnly(deal.status)}
                                canUpload={canUploadDocs(statusInfo?.status_info)}
                                canDelete={user?.organization_id === deal?.organization_id} // Strict: Only Deal Owner can delete
                                t={t}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8 border-2 border-dashed rounded-lg">
                        <SvgIcon src="folder.svg" className="h-12 w-12 mx-auto opacity-50 mb-3" />
                        <p className="text-sm text-muted-foreground">{t('dealRoom.documents.empty')}</p>
                        <p className="text-xs text-muted-foreground mt-1 mb-4">
                          {canUploadDocs(statusInfo?.status_info)
                            ? t('dealRoom.documents.uploadHelp')
                            : t('dealRoom.documents.uploadDisabledHelp')}
                        </p>
                        {canUploadDocs(statusInfo?.status_info) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => fileInputRef.current?.click()}
                          >
                            <Upload className="h-4 w-4 mr-1" />
                            {t('dealRoom.documents.uploadFirst')}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Contract Tab - fixed height + scroll like Messages/Documents */}
            <TabsContent value="contract" className="mt-4 flex-1 flex flex-col min-h-0 overflow-hidden data-[state=inactive]:hidden">
              <Card className="flex flex-col flex-1 min-h-0 overflow-hidden">
                <CardHeader className="flex-shrink-0 border-b py-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileSignature className="h-5 w-5" />
                      {t('dealRoom.tabs.contract')}
                    </CardTitle>
                    {contract && (
                      <Badge
                        variant="outline"
                        className={
                          contract.status === 'finalized'
                            ? 'bg-blue-500/10 text-blue-600 border-blue-500/30'
                            : 'bg-green-500/10 text-green-600 border-green-500/30'
                        }
                      >
                        {contract.status === 'finalized' ? 'Finalized' : 'Draft'}
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="flex flex-col flex-1 min-h-0 overflow-hidden p-4">
                  <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0">
                    {contractLoading ? (
                      <div className="space-y-4">
                        <Skeleton className="h-8 w-48" />
                        <Skeleton className="h-40 w-full" />
                      </div>
                    ) : contract ? (
                      // Contract exists - display it
                      <div className="space-y-4">
                        {/* AI Label — only for drafts; show finalized label otherwise */}
                        {contract.status !== 'finalized' ? (
                          <div className="flex items-center gap-2 p-3 rounded-lg bg-accent/5 border border-accent/20">
                            <SvgIcon src="bulb.svg" className="h-5 w-5" />
                            <span className="text-sm font-medium text-accent">{t('dealRoom.contract.aiLabel')}</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-500/5 border border-blue-500/20">
                            <FileSignature className="h-5 w-5 text-blue-600" />
                            <span className="text-sm font-medium text-blue-600">{t("dealroom.finalizedContract") || "Finalized Contract — Ready for Signature"}</span>
                          </div>
                        )}

                        {/* Contract Notice for ambiguous clauses */}
                        {contract.hasAmbiguousClauses && (
                          <Alert className="bg-amber-50 border-amber-200">
                            <Info className="h-4 w-4 text-amber-600" />
                            <AlertDescription className="text-amber-800 text-sm">
                              {t('dealRoom.contract.notice')}
                            </AlertDescription>
                          </Alert>
                        )}

                        {/* Language Toggle */}
                        <div className="flex items-center gap-2">
                          <Label className="text-sm text-muted-foreground">{t('dealRoom.contract.language')}:</Label>
                          <Select
                            value={selectedContractLanguage}
                            onValueChange={setSelectedContractLanguage}
                          >
                            <SelectTrigger className="w-40">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {contract.languages?.map(lang => {
                                const langInfo = CONTRACT_LANGUAGES.find(l => l.code === lang);
                                return (
                                  <SelectItem key={lang} value={lang}>
                                    {langInfo ? `${langInfo.name}` : lang.toUpperCase()}
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copyContractContent(selectedContractLanguage)}
                            className="ml-auto"
                            data-testid="copy-contract-button"
                          >
                            <Copy className="h-4 w-4 mr-1" />
                            {t('dealRoom.contract.copy')} {selectedContractLanguage.toUpperCase()}
                          </Button>
                        </div>

                        {/* Contract Content — edit mode or read-only view */}
                        {isEditingContract ? (
                          <div className="space-y-2">
                            <p className="text-xs text-muted-foreground">
                              Editing <span className="font-semibold uppercase">{selectedContractLanguage}</span> version. Switch language above to edit other versions.
                            </p>
                            <Textarea
                              className="h-[400px] font-mono text-sm resize-none"
                              value={editedContractContent[selectedContractLanguage] || ''}
                              onChange={(e) =>
                                setEditedContractContent(prev => ({
                                  ...prev,
                                  [selectedContractLanguage]: e.target.value
                                }))
                              }
                              data-testid="contract-edit-textarea"
                            />
                          </div>
                        ) : (
                          <ScrollArea className="h-[400px] border rounded-lg p-4 bg-muted/20">
                            <pre className="whitespace-pre-wrap font-mono text-sm" data-testid="contract-content">
                              {contract.content?.[selectedContractLanguage] || t('common.loading')}
                            </pre>
                          </ScrollArea>
                        )}

                        {/* Contract Metadata */}
                        <div className="grid grid-cols-2 gap-4 text-sm p-3 rounded-lg bg-muted/30">
                          <div>
                            <p className="text-xs text-muted-foreground">{t('dealRoom.contract.type')}</p>
                            <p className="font-medium capitalize">{contract.contract_type?.replace('_', ' ')}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">{t('dealRoom.contract.generated')}</p>
                            <p className="font-medium text-xs">
                              {formatDealRoomDate(contract.created_at)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">{t('dealRoom.contract.languages')}</p>
                            <p className="font-medium">
                              {contract.languages?.map(lang => {
                                const langInfo = CONTRACT_LANGUAGES.find(l => l.code === lang);
                                return langInfo ? langInfo.name : lang.toUpperCase();
                              }).join(', ')}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">{t('dealRoom.contract.status')}</p>
                            <Badge variant="secondary" className="capitalize">{contract.status}</Badge>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-2">
                          {isEditingContract ? (
                            // Edit mode: Save and Cancel
                            <>
                              <Button
                                onClick={handleSaveEdit}
                                disabled={updateContractMutation.isPending}
                                data-testid="save-contract-button"
                              >
                                {updateContractMutation.isPending ? (
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                  <Check className="h-4 w-4 mr-2" />
                                )}
                                Save Changes
                              </Button>
                              <Button
                                variant="outline"
                                onClick={handleCancelEdit}
                                disabled={updateContractMutation.isPending}
                                data-testid="cancel-edit-contract-button"
                              >
                                <X className="h-4 w-4 mr-2" />
                                Cancel
                              </Button>
                            </>
                          ) : (
                            // View mode: Edit, Regenerate, Finalize (creator + draft only)
                            <>
                              {/* Regenerate Button */}
                              {canGenerateContract && (deal.status === 'draft' || deal.status === 'in_progress') && contract.status !== 'finalized' && (
                                <Button
                                  variant="outline"
                                  onClick={handleGenerateContract}
                                  disabled={generateContractMutation.isPending}
                                  data-testid="regenerate-contract-button"
                                >
                                  {generateContractMutation.isPending ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  ) : (
                                    <RefreshCw className="h-4 w-4 mr-2" />
                                  )}
                                  {t('dealRoom.contract.regenerate')}
                                </Button>
                              )}

                              {/* Edit Button (creator + draft only) */}
                              {canGenerateContract && (deal.status === 'draft' || deal.status === 'in_progress') && contract.status !== 'finalized' && (
                                <Button
                                  variant="outline"
                                  onClick={handleStartEdit}
                                  data-testid="edit-contract-button"
                                >
                                  <FileText className="h-4 w-4 mr-2" />
                                  Edit Draft
                                </Button>
                              )}

                              {/* Finalize Button (creator + draft only) */}
                              {canGenerateContract && (deal.status === 'draft' || deal.status === 'in_progress') && contract.status !== 'finalized' && (
                                <Button
                                  onClick={() => setFinalizeConfirmOpen(true)}
                                  data-testid="finalize-contract-button"
                                >
                                  <FileSignature className="h-4 w-4 mr-2" />
                                  Finalize & Send for Signature
                                </Button>
                              )}

                              {/* Download PDF Button (all parties, finalized and signed) */}
                              {(contract.status === 'finalized' || contract.status === 'signed') && (
                                <Button
                                  variant="outline"
                                  onClick={handleDownloadPdf}
                                  disabled={isDownloadingPdf}
                                  data-testid="download-contract-pdf-button"
                                >
                                  {isDownloadingPdf ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  ) : (
                                    <Download className="h-4 w-4 mr-2" />
                                  )}
                                  {isDownloadingPdf ? 'Generating PDF…' : `Download PDF (${selectedContractLanguage.toUpperCase()})`}
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    ) : (
                      // No contract - show generation option
                      <div className="text-center py-12 border-2 border-dashed rounded-lg">
                        <FileSignature className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
                        <h3 className="text-lg font-medium mb-2">{t('dealRoom.contract.noContract')}</h3>
                        <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
                          {t('dealRoom.contract.noContractDesc')}
                        </p>

                        {isReadOnly(deal.status) ? (
                          <p className="text-sm text-muted-foreground">
                            {t('dealRoom.contract.notAvailable', { status: deal.status })}
                          </p>
                        ) : isPendingSignature(deal.status) ? (
                          <Alert className="max-w-md mx-auto">
                            <Lock className="h-4 w-4" />
                            <AlertDescription>
                              {t('dealRoom.contract.locked')}
                            </AlertDescription>
                          </Alert>
                        ) : !canGenerateContract || (deal.status !== 'draft' && deal.status !== 'in_progress') ? (
                          <Alert className="max-w-md mx-auto">
                            <Info className="h-4 w-4" />
                            <AlertDescription>
                              Only the deal creator can generate contracts. You will be able to view and sign the contract once it's generated.
                            </AlertDescription>
                          </Alert>
                        ) : (
                          <Button
                            onClick={handleGenerateContract}
                            disabled={generateContractMutation.isPending}
                            className="gap-2"
                            data-testid="generate-contract-button"
                          >
                            {generateContractMutation.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Sparkles className="h-4 w-4" />
                            )}
                            {t('dealRoom.contract.generate')}
                          </Button>
                        )}

                        {/* Pro plan badge for AI contract generation */}
                        {orgPlanInfo?.plan && orgPlanInfo.plan !== 'pro' && (
                          <div className="mt-4 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                            <Crown className="h-4 w-4 text-yellow-500" />
                            <span>{t('dealRoom.contract.requiresPlan') || 'Upgrade your plan for more AI contracts'}</span>
                          </div>
                        )}

                        {/* Manual contract option — Basic plan users only */}
                        {canGenerateContract && orgPlanInfo?.plan && orgPlanInfo.plan !== 'pro' && (
                          <div className="mt-4">
                            <div className="flex items-center gap-3 mb-3">
                              <div className="flex-1 h-px bg-border" />
                              <span className="text-xs text-muted-foreground">or</span>
                              <div className="flex-1 h-px bg-border" />
                            </div>
                            <Button
                              variant="outline"
                              onClick={() => setManualContractDialogOpen(true)}
                              className="gap-2"
                              data-testid="create-manual-contract-button"
                            >
                              <FileText className="h-4 w-4" />
                              Write Contract Manually
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="meetings" className="mt-4 flex-1 flex flex-col min-h-0 overflow-hidden data-[state=inactive]:hidden">
              <div className="space-y-4 overflow-y-auto">
                {/* Header with create button */}
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-foreground">{t("dealroom.meetings") || "Meetings"}</h3>
                  <Button
                    size="sm"
                    onClick={async () => {
                      try {
                        const res = await api.post('/meetings', {
                          title: `${deal?.title || 'Deal'} Meeting`,
                          type: 'instant',
                          deal_id: dealId,
                          duration_minutes: 30,
                        });
                        refetchMeetings();
                        window.open(`/meet/${res.join_code}`, '_blank');
                      } catch (err) {
                        toast.error(t('dealroom.failedCreateMeeting') || 'Failed to create meeting');
                      }
                    }}
                  >
                    <Video className="h-4 w-4 mr-2" />
                    {t("dealroom.startMeeting") || "Start Meeting"}
                  </Button>
                </div>

                {/* Meetings list */}
                {dealMeetings.length === 0 ? (
                  <div className="text-center py-12">
                    <Video className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
                    <p className="text-muted-foreground">{t("dealroom.noMeetings") || "No meetings linked to this deal yet"}</p>
                    <p className="text-sm text-muted-foreground/60 mt-1">{t("dealroom.noMeetingsDesc") || "Start a meeting or schedule one to get started"}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {dealMeetings.map((meeting) => (
                      <div
                        key={meeting._id}
                        className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                            meeting.status === 'active' ? 'bg-green-100 dark:bg-green-900' :
                            meeting.status === 'scheduled' ? 'bg-blue-100 dark:bg-blue-900' :
                            'bg-muted'
                          }`}>
                            <Video className={`h-5 w-5 ${
                              meeting.status === 'active' ? 'text-green-600 dark:text-green-400' :
                              meeting.status === 'scheduled' ? 'text-blue-600 dark:text-blue-400' :
                              'text-muted-foreground'
                            }`} />
                          </div>
                          <div>
                            <p className="font-medium text-foreground">{meeting.title}</p>
                            <p className="text-sm text-muted-foreground">
                              {meeting.scheduled_at
                                ? new Date(meeting.scheduled_at).toLocaleString()
                                : meeting.actual_started_at
                                  ? new Date(meeting.actual_started_at).toLocaleString()
                                  : 'No date'}
                              {' · '}{meeting.duration_minutes || 30} min
                              {meeting.participants?.length > 0 && ` · ${meeting.participants.length} participants`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={
                            meeting.status === 'active' ? 'default' :
                            meeting.status === 'scheduled' ? 'secondary' :
                            'outline'
                          }>
                            {meeting.status}
                          </Badge>
                          {meeting.status === 'active' && (
                            <Button size="sm" variant="outline" onClick={() => window.open(`/meet/${meeting.join_code}`, '_blank')}>
                              Join
                            </Button>
                          )}
                          {meeting.status === 'scheduled' && (
                            <Button size="sm" variant="outline" onClick={() => window.open(`/meet/${meeting.join_code}`, '_blank')}>
                              Join
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>



          </Tabs>
        </div>
      </div>

      {/* Spacer so left/right section borders have clear space above browser bottom */}
      <div className="h-12 flex-shrink-0" aria-hidden="true" />

      {/* Document Detail Sheet */}
      <Sheet open={documentSheetOpen} onOpenChange={setDocumentSheetOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="document-detail-sheet">
          {selectedDocument && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  {(() => {
                    const FileIcon = getFileIcon(selectedDocument.mime_type);
                    return <FileIcon className="h-5 w-5 text-primary" />;
                  })()}
                  <span className="truncate">{selectedDocument.original_filename}</span>
                </SheetTitle>
                <SheetDescription>
                  {t('dealRoom.documents.uploaded')} {formatDealRoomDate(selectedDocument.created_at)} • {(selectedDocument.size / 1024).toFixed(1)} KB
                </SheetDescription>
              </SheetHeader>

              {/* Parsing Logic for AI Data */}
              {(() => {
                let aiData = selectedDocument.ai_extracted_data;
                // If it's wrapped in raw_analysis string (Markdown JSON)
                if (aiData?.raw_analysis && typeof aiData.raw_analysis === 'string') {
                  try {
                    const cleanJson = aiData.raw_analysis.replace(/```json\n?|```/g, '').trim();
                    aiData = JSON.parse(cleanJson);
                  } catch (e) {
                    console.error("Failed to parse AI analysis JSON:", e);
                    // Fallback: don't overwrite, just keep as is or set to null
                  }
                }

                // Assign to a variable for use in JSX below
                // We will access 'aiData' in the block below by recalculating or passing it down
                // Actually, doing this inside the JSX flow is messy.
                // Let's use a standard variable assignment before the return if possible, 
                // but since we are inside conditional rendering, we can define it just before usage 
                // or use a helper function.
                // Better approach: I'll wrap the AI section in an IIFE/block with this const.
                return null; // Just distinct separator for my thought process, checking next block
              })()}

              <div className="space-y-6 mt-6">
                {/* Document Preview */}
                <div className="rounded-lg border bg-muted/30 p-4">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">{t('dealRoom.documents.preview')}</p>
                  {selectedDocument.mime_type?.startsWith('image/') ? (
                    <div
                      className="aspect-video bg-muted rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity relative group"
                      onClick={() => handlePreviewClick(selectedDocument)}
                    >
                      {(() => {
                        const isS3 = selectedDocument.storage_path?.startsWith?.('uploads/');
                        const imgSrc = isS3 ? documentPreviewUrl : `${process.env.REACT_APP_BACKEND_URL || ''}/uploads/${selectedDocument.storage_path}`;
                        return imgSrc ? (
                          <>
                            <img
                              src={imgSrc}
                              alt={selectedDocument.original_filename}
                              className="w-full h-full object-contain"
                              data-testid="document-preview-image"
                            />
                            <div className="absolute inset-0 flex items-start justify-end p-2 bg-black/0 group-hover:bg-black/10 transition-colors">
                              <Eye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 drop-shadow-md" />
                            </div>
                          </>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    <div
                      className="aspect-video bg-muted rounded-lg flex items-center justify-center cursor-pointer hover:bg-muted/80 transition-colors group relative"
                      onClick={() => handlePreviewClick(selectedDocument)}
                    >
                      {(() => {
                        const FileIcon = getFileIcon(selectedDocument.mime_type);
                        return (
                          <div className="text-center">
                            <FileIcon className="h-16 w-16 text-muted-foreground/50 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                            <p className="text-sm text-muted-foreground">{selectedDocument.mime_type}</p>
                            <div className="absolute inset-0 flex items-start justify-end p-2 bg-black/0 group-hover:bg-black/5 rounded-lg transition-colors">
                              <Eye className="w-6 h-6 text-foreground/50 opacity-0 group-hover:opacity-100" />
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {/* Document Actions */}
                {/* <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => handleDownload(selectedDocument)}
                    data-testid="download-document-button"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {t('dealRoom.documents.download')}
                  </Button>
                  {!selectedDocument.ai_extracted_data && (
                    <Button
                      className="flex-1"
                      onClick={() => analyzeMutation.mutate(selectedDocument._id)}
                      disabled={analyzeMutation.isPending}
                      data-testid="analyze-document-button"
                    >
                      {analyzeMutation.isPending ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <SvgIcon src="bulb.svg" className="h-4 w-4 mr-2" />
                      )}
                      {t('dealRoom.documents.analyzeWithAI')}
                    </Button>
                  )}
                  <Button
                    variant="destructive"
                    size="icon"
                    onClick={() => handleDeleteDocument(selectedDocument._id)}
                    disabled={deleteMutation.isPending}
                    aria-label={t('dealRoom.documents.delete')}
                    data-testid="delete-document-button"
                  >
                    {deleteMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </div> */}

                {/* AI Analysis Results */}
                {/* AI Analysis Results */}
                {(() => {
                  let aiData = selectedDocument.ai_extracted_data;
                  // Parsing Logic: If raw_analysis string exists, parse it.
                  if (aiData?.raw_analysis && typeof aiData.raw_analysis === 'string') {
                    try {
                      const cleanJson = aiData.raw_analysis.replace(/```json\n?|```/g, '').trim();
                      aiData = JSON.parse(cleanJson);
                    } catch (e) {
                      console.error("Failed to parse AI analysis JSON:", e);
                      // On failure, aiData remains as object with raw_analysis, which won't match fields below.
                    }
                  }

                  if (aiData && aiData.document_type) {
                    return (
                      <div className="space-y-4">
                        <div className="flex items-center gap-2">
                          <SvgIcon src="bulb.svg" className="h-5 w-5" />
                          <p className="text-sm font-medium text-accent">{t('dealRoom.documents.aiAnalysis')}</p>
                        </div>

                        {/* Document Type & Confidence */}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-3 rounded-lg bg-muted/50">
                            <p className="text-xs text-muted-foreground">{t('dealRoom.documents.type')}</p>
                            <p className="font-medium capitalize">
                              {aiData.document_type || 'Unknown'}
                            </p>
                          </div>
                          <div className="p-3 rounded-lg bg-muted/50">
                            <p className="text-xs text-muted-foreground">{t('dealRoom.documents.confidence')}</p>
                            <p className={`font-medium ${getConfidenceColor(aiData.confidence)}`}>
                              {aiData.confidence
                                ? `${(aiData.confidence * 100).toFixed(0)}%`
                                : 'N/A'}
                            </p>
                          </div>
                        </div>

                        {/* Summary */}
                        {aiData.summary && (
                          <div className="p-3 rounded-lg bg-accent/5 border border-accent/20">
                            <p className="text-xs text-muted-foreground mb-1">{t('dealRoom.documents.summary')}</p>
                            <p className="text-sm">{aiData.summary}</p>
                          </div>
                        )}

                        {/* Extracted Fields */}
                        {aiData.extracted_fields &&
                          Object.keys(aiData.extracted_fields).length > 0 && (
                            <div className="p-3 rounded-lg bg-muted/50">
                              <p className="text-xs text-muted-foreground mb-3">{t('dealRoom.documents.extractedFields')}</p>
                              <div className="space-y-2">
                                {Object.entries(aiData.extracted_fields).map(([key, value]) => (
                                  <div key={key} className="flex justify-between items-center py-1 border-b border-border/50 last:border-0">
                                    <span className="text-sm text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
                                    {/* Handle nested objects (like services in the example) or strings */}
                                    <span className="text-sm font-medium text-right truncate max-w-[150px]">
                                      {typeof value === 'object' && value !== null
                                        ? JSON.stringify(value).slice(0, 50) + (JSON.stringify(value).length > 50 ? '...' : '')
                                        : String(value)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                        {/* Issues */}
                        {aiData.issues &&
                          aiData.issues.length > 0 && (
                            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                              <p className="text-xs text-destructive font-medium mb-2">{t('dealRoom.documents.issuesDetected')}</p>
                              <ul className="space-y-1">
                                {aiData.issues.map((issue, idx) => (
                                  <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                                    <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
                                    {issue}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Not Analyzed Yet */}
                {!selectedDocument.ai_extracted_data && (
                  <div className="p-6 rounded-lg border-2 border-dashed text-center">
                    <SvgIcon src="bulb.svg" className="h-10 w-10 mx-auto mb-3 opacity-50" />
                    <p className="text-sm font-medium">{t('dealRoom.documents.notAnalyzed')}</p>
                    <p className="text-xs text-muted-foreground mt-1 mb-4">
                      {t('dealRoom.documents.notAnalyzedHelp')}
                    </p>
                    <Button
                      onClick={() => analyzeMutation.mutate(selectedDocument._id)}
                      disabled={analyzeMutation.isPending}
                    >
                      {analyzeMutation.isPending ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <SvgIcon src="bulb.svg" className="h-4 w-4 mr-2" />
                      )}
                      {t('dealRoom.documents.analyzeDocument')}
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Full Screen Document Preview Dialog */}
      <Dialog open={fullScreenPreviewOpen} onOpenChange={setFullScreenPreviewOpen}>
        <DialogContent className="max-w-5xl h-[90vh] flex flex-col p-0">
          <DialogHeader className="p-4 border-b">
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                {selectedDocument && (() => {
                  const FileIcon = getFileIcon(selectedDocument.mime_type);
                  return <FileIcon className="h-5 w-5 text-primary" />;
                })()}
                <span className="truncate max-w-[calc(100vw-200px)]">{selectedDocument?.original_filename}</span>
              </DialogTitle>
            </div>
          </DialogHeader>
          <div className="flex-1 bg-muted/20 overflow-hidden relative flex items-center justify-center p-4">
            {selectedDocument && (
              <>
                {selectedDocument.mime_type?.startsWith('image/') ? (
                  <img
                    src={(() => {
                      const isS3 = selectedDocument.storage_path?.startsWith?.('uploads/');
                      return isS3 ? documentPreviewUrl : `${process.env.REACT_APP_BACKEND_URL || ''}/uploads/${selectedDocument.storage_path}`;
                    })()}
                    alt={selectedDocument.original_filename}
                    className="max-w-full max-h-full object-contain"
                  />
                ) : selectedDocument.mime_type === 'application/pdf' ? (
                  <iframe
                    src={(() => {
                      const isS3 = selectedDocument.storage_path?.startsWith?.('uploads/');
                      return isS3 ? documentPreviewUrl : `${process.env.REACT_APP_BACKEND_URL || ''}/uploads/${selectedDocument.storage_path}`;
                    })()}
                    className="w-full h-full rounded shadow-sm border"
                    title="PDF Preview"
                  />
                ) : (
                  <div className="text-center">
                    <p>{t("dealroom.previewNotSupported") || "Preview not supported"}</p>
                  </div>
                )}
              </>
            )}
            {(!documentPreviewUrl && selectedDocument?.storage_path?.startsWith?.('uploads/')) && (
              <div className="absolute inset-0 flex items-center justify-center bg-background/50 backdrop-blur-sm z-10">
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
              </div>
            )}
          </div>
          <DialogFooter className="p-4 border-t bg-background">
            <Button variant="outline" onClick={() => setFullScreenPreviewOpen(false)}>
              {t('common.close')}
            </Button>
            <Button onClick={() => handleDownload(selectedDocument)}>
              <Download className="h-4 w-4 mr-2" />
              {t('dealRoom.documents.download')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Transition Confirmation Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedNewStatus && (() => {
                const StatusIcon = statusIcons[selectedNewStatus.status] || FileText;
                return <StatusIcon className="h-5 w-5" />;
              })()}
              {t('dealRoom.status.dialog.title')}
            </DialogTitle>
            <DialogDescription>
              {selectedNewStatus
                ? `${t('dealRoom.status.dialog.to')}: ${selectedNewStatus.label}`
                : ''}
            </DialogDescription>
          </DialogHeader>

          {selectedNewStatus && (
            <div className="space-y-4 py-4">
              {/* Show what will happen */}
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-sm font-medium mb-1">{selectedNewStatus.label}</p>
                <p className="text-xs text-muted-foreground">{selectedNewStatus.description}</p>
              </div>

              {/* Optional reason */}
              <div className="space-y-2">
                <Label htmlFor="transition-reason">{t('dealRoom.status.dialog.reason')}</Label>
                <Textarea
                  id="transition-reason"
                  placeholder={t('dealRoom.status.dialog.reasonPlaceholder')}
                  value={transitionReason}
                  onChange={(e) => setTransitionReason(e.target.value)}
                  rows={2}
                />
              </div>

              {/* Warning for final states */}
              {(selectedNewStatus.status === 'closed' || selectedNewStatus.status === 'cancelled') && (
                <Alert className="border-destructive/50 bg-destructive/5">
                  <AlertCircle className="h-4 w-4 text-destructive" />
                  <AlertDescription className="text-destructive">
                    {t('dealRoom.status.finalWarning')}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setStatusDialogOpen(false);
                setSelectedNewStatus(null);
                setTransitionReason('');
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={confirmStatusTransition}
              disabled={statusTransitionMutation.isPending}
              variant={selectedNewStatus?.status === 'cancelled' ? 'destructive' : 'default'}
            >
              {statusTransitionMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              {t('dealRoom.status.dialog.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contract Generation Dialog */}
      <Dialog open={contractDialogOpen} onOpenChange={setContractDialogOpen}>
        <DialogContent className="w-[95vw] max-w-md max-h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="p-6 pb-2 flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <FileSignature className="h-5 w-5" />
              {t('dealRoom.contract.dialog.title')}
            </DialogTitle>
            <DialogDescription>
              {t('dealRoom.contract.dialog.desc')}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-2">
            <div className="space-y-4 py-2">
              {/* Contract Type - Fixed for MVP */}
              <div className="space-y-2">
                <Label>{t('dealRoom.contract.dialog.type')}</Label>
                <div className="p-3 rounded-lg bg-muted">
                  <p className="font-medium">{t('dealRoom.contract.purchaseAgreement')}</p>
                  <p className="text-xs text-muted-foreground">{t('dealRoom.contract.purchaseAgreementDesc')}</p>
                </div>
              </div>

              {/* Languages */}
              <div className="space-y-2">
                <Label>{t('dealRoom.contract.dialog.languages')}</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  {t('dealRoom.contract.dialog.languagesHelp')}
                </p>
                <div className="flex gap-2">
                  <Select
                    value={contractLanguages[0]}
                    onValueChange={(v) => setContractLanguages([v, contractLanguages[1]])}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder={t('dealRoom.contract.dialog.primary')} />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTRACT_LANGUAGES.map(lang => (
                        <SelectItem key={lang.code} value={lang.code}>
                          {lang.name} ({lang.nativeName})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={contractLanguages[1]}
                    onValueChange={(v) => setContractLanguages([contractLanguages[0], v])}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder={`${t('dealRoom.contract.dialog.secondary')} (${t('common.optional')})`} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {CONTRACT_LANGUAGES.filter(lang => lang.code !== contractLanguages[0]).map(lang => (
                        <SelectItem key={lang.code} value={lang.code}>
                          {lang.name} ({lang.nativeName})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Special Terms */}
              <div className="space-y-2">
                <Label htmlFor="special-terms">{t('dealRoom.contract.dialog.terms')} ({t('common.optional')})</Label>
                <Textarea
                  id="special-terms"
                  placeholder={t('dealRoom.contract.dialog.termsPlaceholder')}
                  value={contractSpecialTerms}
                  onChange={(e) => setContractSpecialTerms(e.target.value)}
                  rows={3}
                />
              </div>

              {/* AI Notice */}
              <div className="flex items-start gap-2 p-3 rounded-lg bg-accent/5 border border-accent/20">
                <SvgIcon src="bulb.svg" className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-muted-foreground">
                  <p className="font-medium text-foreground mb-1">{t('dealRoom.contract.aiLabel')}</p>
                  <p>{t('dealRoom.contract.dialog.disclaimer')}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 pt-2 flex-shrink-0 border-t bg-background">
            <DialogFooter className="pt-2">
              <Button variant="outline" onClick={() => setContractDialogOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={() => submitContractGeneration()}
                disabled={generateContractMutation.isPending}
              >
                {generateContractMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('dealRoom.contract.dialog.generating')}
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    {t('dealRoom.contract.dialog.generate')}
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual Contract Creation Dialog — Basic plan users */}
      <Dialog open={manualContractDialogOpen} onOpenChange={setManualContractDialogOpen}>
        <DialogContent className="w-[95vw] max-w-md max-h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="p-6 pb-2 flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Write Contract Manually
            </DialogTitle>
            <DialogDescription>
              Choose the language(s) for your contract. You will type the content in the editor.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-2">
            <div className="space-y-4 py-2">
              {/* Languages — same picker as AI generation dialog */}
              <div className="space-y-2">
                <Label>{t('dealRoom.contract.dialog.languages')}</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  {t('dealRoom.contract.dialog.languagesHelp')}
                </p>
                <div className="flex gap-2">
                  <Select
                    value={contractLanguages[0]}
                    onValueChange={(v) => setContractLanguages([v, contractLanguages[1]])}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder={t('dealRoom.contract.dialog.primary')} />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTRACT_LANGUAGES.map(lang => (
                        <SelectItem key={lang.code} value={lang.code}>
                          {lang.name} ({lang.nativeName})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={contractLanguages[1]}
                    onValueChange={(v) => setContractLanguages([contractLanguages[0], v])}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder={`${t('dealRoom.contract.dialog.secondary')} (${t('common.optional')})`} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {CONTRACT_LANGUAGES.filter(lang => lang.code !== contractLanguages[0]).map(lang => (
                        <SelectItem key={lang.code} value={lang.code}>
                          {lang.name} ({lang.nativeName})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Info notice */}
              <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50 border text-xs text-muted-foreground">
                <Info className="h-4 w-4 shrink-0 mt-0.5" />
                <p>
                  An empty contract draft will be created. You can type your contract text directly in the editor,
                  then finalize and send for signature when ready.
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 pt-2 flex-shrink-0 border-t bg-background">
            <DialogFooter className="pt-2">
              <Button variant="outline" onClick={() => setManualContractDialogOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={() => {
                  const validLanguages = contractLanguages.filter(l => l !== 'none');
                  createManualContractMutation.mutate(validLanguages.length > 0 ? validLanguages : ['en']);
                }}
                disabled={createManualContractMutation.isPending}
              >
                {createManualContractMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating…
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4 mr-2" />
                    Create Empty Contract
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Generating Contract loader — shown whenever contract generation API is in progress */}
      <Dialog
        open={generateContractMutation.isPending && !generatingLoaderDismissed}
        onOpenChange={(open) => { if (!open) setGeneratingLoaderDismissed(true); }}
      >
        <DialogContent
          className="sm:max-w-md"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          showCloseButton={true}
        >
          <div className="flex flex-col items-center justify-center py-10 gap-4">
            <Loader2 className="h-12 w-12 animate-spin text-primary" aria-hidden="true" />
            <div className="text-center space-y-1">
              <p className="text-sm font-medium">{t('dealRoom.contract.dialog.generating') || 'Generating contract...'}</p>
              <p className="text-xs text-muted-foreground">{t("dealroom.aiMayTakeMoment") || "This may take a moment."}</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Document Analysis Warning Dialog */}
      <Dialog open={analysisWarningOpen} onOpenChange={setAnalysisWarningOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-500" />
              Document Analysis Recommended
            </DialogTitle>
            <DialogDescription>
              {pendingAnalysisData?.message}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Analyzed Documents */}
            {pendingAnalysisData?.analyzed_documents && pendingAnalysisData.analyzed_documents.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>{t("dealroom.aiAnalysisGenerated") || "AI analysis has been generated for the following documents:"}</span>
                </div>
                <ul className="list-disc list-inside text-sm text-muted-foreground ml-6 space-y-1">
                  {pendingAnalysisData.analyzed_documents.map((doc) => (
                    <li key={doc.id}>{doc.filename}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Unanalyzed Documents */}
            {pendingAnalysisData?.unanalyzed_documents && pendingAnalysisData.unanalyzed_documents.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <AlertCircle className="h-4 w-4 text-yellow-500" />
                  <span>{t("dealroom.docsUnanalyzed") || "The following documents remain unanalyzed:"}</span>
                </div>
                <ul className="list-disc list-inside text-sm text-muted-foreground ml-6 space-y-1">
                  {pendingAnalysisData.unanalyzed_documents.map((doc) => (
                    <li key={doc.id}>{doc.filename}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Progress Indicator (shown during analysis) */}
            {isAnalyzingDocs && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-accent/5 border border-accent/20">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                <div className="flex-1">
                  <p className="text-sm font-medium">{t("dealroom.analyzingDocs") || "Analyzing documents..."}</p>
                  <p className="text-xs text-muted-foreground">
                    {analysisProgress.current} of {analysisProgress.total} completed
                  </p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setAnalysisWarningOpen(false);
                submitContractGeneration(true);
              }}
              disabled={isAnalyzingDocs}
              className="w-full sm:w-auto"
            >
              Proceed Without Analysis
            </Button>
            <Button
              onClick={handleRunAnalysisAndGenerate}
              disabled={isAnalyzingDocs}
              className="w-full sm:w-auto"
            >
              {isAnalyzingDocs ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Analyze & Generate
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Business Plan Upgrade Dialog */}
      <Dialog open={upgradeDialogOpen} onOpenChange={setUpgradeDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-yellow-500" />
              {t('dealRoom.upgrade.title')}
            </DialogTitle>
            <DialogDescription>
              {t('dealRoom.upgrade.desc')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="p-4 rounded-lg border bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-950/20 dark:to-orange-950/20">
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                {t('dealRoom.upgrade.features')}
              </h4>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• {t('dealRoom.upgrade.feature1')}</li>
                <li>• {t('dealRoom.upgrade.feature2')}</li>
                <li>• {t('dealRoom.upgrade.feature3')}</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUpgradeDialogOpen(false)}>
              {t('common.close')}
            </Button>
            <Button onClick={() => {
              setUpgradeDialogOpen(false);
              navigate('/settings?tab=plan');
            }}>
              <Crown className="h-4 w-4 mr-2" />
              {t('dealRoom.upgrade.button')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule call / recording Dialog */}
      <Dialog open={scheduleModalOpen} onOpenChange={setScheduleModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('schedule.title') || 'Schedule call or recording'}</DialogTitle>
            <DialogDescription>
              {t('schedule.description') || 'Pick a date and time. You can join the call when it\'s time.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="schedule-datetime">{t('schedule.dateTime') || 'Date & time'}</Label>
              <Input
                id="schedule-datetime"
                type="datetime-local"
                value={scheduleDateTime}
                onChange={(e) => setScheduleDateTime(e.target.value)}
                min={new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleModalOpen(false)}>
              {t('common.cancel') || 'Cancel'}
            </Button>
            <Button
              onClick={() => {
                if (!scheduleDateTime.trim()) {
                  toast.error(t('schedule.chooseDateTime') || 'Please choose date and time');
                  return;
                }
                const selectedTime = new Date(scheduleDateTime).getTime();
                if (selectedTime < Date.now()) {
                  toast.error('Cannot schedule a call in the past');
                  return;
                }
                const iso = new Date(scheduleDateTime).toISOString();
                createScheduleMutation.mutate({ deal_id: dealId, scheduled_at: iso, type: 'call' });
              }}
              disabled={createScheduleMutation.isPending}
            >
              {createScheduleMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {t('schedule.create') || 'Schedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Signature Pad Dialog */}
      <SignaturePad
        open={signatureDialogOpen}
        onOpenChange={setSignatureDialogOpen}
        onSign={handleSignContract}
        isLoading={signingContract}
        signerName={user?.name || ''}
        contractTitle={contract?.title || deal?.title || ''}
      />

      {/* Signatures List Modal */}
      <SignaturesListModal
        open={signaturesListOpen}
        onOpenChange={setSignaturesListOpen}
        deal={deal}
        clients={clients}
        signatures={signatures}
      />

      {/* Finalize Contract Confirmation Dialog */}
      <Dialog open={finalizeConfirmOpen} onOpenChange={setFinalizeConfirmOpen}>
        <DialogContent data-testid="finalize-contract-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSignature className="h-5 w-5" />
              Finalize Contract for Signature
            </DialogTitle>
            <DialogDescription>
              This will lock the contract and send it for signature. Once finalized, the contract cannot be edited or regenerated.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <Alert className="bg-amber-50 border-amber-200">
              <Info className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800 text-sm">
                {t("dealroom.finalizeWarning") || "This action cannot be undone. The deal status will change to Pending Signature and all parties will be able to sign the contract."}
              </AlertDescription>
            </Alert>
            <p className="text-sm text-muted-foreground">
              Make sure you have reviewed and edited the contract draft before finalizing.
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setFinalizeConfirmOpen(false)}
              disabled={finalizeContractMutation.isPending}
            >
              Go Back & Review
            </Button>
            <Button
              onClick={handleFinalizeContract}
              disabled={finalizeContractMutation.isPending}
              data-testid="confirm-finalize-button"
            >
              {finalizeContractMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FileSignature className="h-4 w-4 mr-2" />
              )}
              Yes, Finalize Contract
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deal Invoice Modal — Create escrow invoices when deal is pending_payment */}
      <DealInvoiceModal
        open={invoiceModalOpen}
        onClose={() => setInvoiceModalOpen(false)}
        deal={deal}
        clients={clients}
        currentUser={user}
      />

      
    </div>
  );
}

// Document Card Component
function DocumentCard({ doc, onClick, onAnalyze, onDownload, onDelete, onReadAloud, ttsPlaying, isAnalyzing, getFileIcon, isReadOnly = false, canUpload = true, canDelete = false, t }) {
  const FileIcon = getFileIcon(doc.mime_type);

  return (
    <div
      className="p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors cursor-pointer group"
      data-testid={`document-card-${doc._id}`}
      onClick={onClick}
    >
      {/* On small screens: content full width (actions on second row). On sm+: single row with actions on hover. */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 rounded bg-muted flex items-center justify-center flex-shrink-0">
            <FileIcon className="h-5 w-5 text-muted-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate" title={doc.original_filename}>{doc.original_filename}</p>
            <p className="text-xs text-muted-foreground">
              {(doc.size / 1024).toFixed(1)} KB • {formatDealRoomDate(doc.created_at)}
              {doc.uploaded_by_name && (
                <span className="ml-2 text-muted-foreground/80">• Uploaded by: {doc.uploaded_by_name}</span>
              )}
            </p>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {doc.ai_extracted_data ? (
                <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/30 whitespace-nowrap">
                  <CheckCircle className="h-3 w-3 mr-1 flex-shrink-0" />
                  <span>{t ? t('dealRoom.documents.analyzed') : 'Analyzed'}</span>
                </Badge>
              ) : (
                <Badge variant="outline" className="bg-yellow-500/10 text-yellow-600 border-yellow-500/30 whitespace-nowrap">
                  <span>{t ? t('dealRoom.documents.pending') : 'Pending'}</span>
                </Badge>
              )}
              {doc.ai_extracted_data?.document_type && (
                <Badge variant="secondary" className="capitalize">
                  {doc.ai_extracted_data.document_type}
                </Badge>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0 justify-end sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
          {/* Read Aloud button */}
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            onClick={(e) => { e.stopPropagation(); onReadAloud(); }}
            disabled={ttsPlaying}
            aria-label={t ? t('documents.readAloud.title') : 'Read aloud'}
            title={t ? t('documents.readAloud.title') : 'Read aloud'}
          >
            {ttsPlaying ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Volume2 className="h-4 w-4" />
            )}
          </Button>
          {/* Download is always available */}
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            onClick={(e) => { e.stopPropagation(); onDownload(); }}
            aria-label={t ? t('dealRoom.documents.download') : 'Download'}
          >
            <Download className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            onClick={(e) => { e.stopPropagation(); onClick(); }}
            aria-label="View details"
          >
            <Eye className="h-4 w-4" />
          </Button>
          {/* Delete only if permitted (Deal Owner) and not read-only */}
          {!isReadOnly && canDelete && (
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 text-destructive hover:text-destructive"
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              aria-label={t ? t('dealRoom.documents.delete') : 'Delete'}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Quick AI Summary Preview */}
      {doc.ai_extracted_data?.summary && (
        <div className="mt-3 p-2 rounded bg-accent/5 border border-accent/10">
          <p className="text-xs text-muted-foreground line-clamp-2">
            {doc.ai_extracted_data.summary}
          </p>
        </div>
      )}
    </div>
  );
}
